/**
 * Eagle vehicle fitment.
 *
 * One module for the whole theme: the garage (one saved vehicle), the pure
 * matching and narrowing functions, the make/model alias table, and the
 * custom elements that use them. Loaded once per page as an ES module.
 *
 * The pure functions at the top have no DOM dependency so they can be unit
 * tested with `node --test` against the real `custom.fitment` documents.
 *
 * Data contract: docs/FAMILY-PRODUCTS-THEME-SPEC.md §3.
 */

/**
 * @typedef {Object} FitmentEntry
 * @property {string} make
 * @property {string} model
 * @property {number} from
 * @property {number} to
 * @property {string[]} cab   Empty array means the row names no cab: any cab.
 * @property {string} note    "" when blank.
 * @property {string[]} skus
 */

/**
 * @typedef {Object} FitmentDoc
 * @property {number} version
 * @property {FitmentEntry[]} vehicles
 * @property {string[]} universal
 */

/**
 * @typedef {Object} Variant
 * @property {number} id
 * @property {string} sku
 * @property {string} label
 * @property {number} price          In cents.
 * @property {boolean} available
 * @property {string|null} box
 * @property {string[]|null} cab
 * @property {string|null} style
 * @property {string|null} bulb
 * @property {string|null} trim
 */

/**
 * @typedef {Object} Vehicle
 * @property {number} year
 * @property {string} make
 * @property {string} model
 */

/*
 * TEMPORARY, 2026-09-18. The Outdoorsman Canopy's rows in AIMS say Make
 * "Dodge", Model "RAM 1500"; fifteen other families say Make "RAM", Model
 * "1500". To the matching rule those are different vehicles.
 *
 * Delete this table once the two canopy parts (EA735223, EA735227) are
 * corrected at the source — see docs/FAMILY-PRODUCTS-THEME-SPEC.md §4.7 and
 * §9.1 item 12 — then re-run tests A8 and G1.
 *
 * Do NOT generalise this into fuzzy matching. A silent merge of makes or
 * models is how a wrong part gets sold.
 */
export const VEHICLE_ALIASES = [{ from: ['dodge', 'ram 1500'], to: ['RAM', '1500'] }];

const clean = (value) => String(value == null ? '' : value).trim();

/**
 * Applies the alias table and returns both the display form and a comparison key.
 * @param {string} make
 * @param {string} model
 * @returns {{ make: string, model: string, key: string }}
 */
export function canon(make, model) {
  let displayMake = clean(make);
  let displayModel = clean(model);
  const lowerMake = displayMake.toLowerCase();
  const lowerModel = displayModel.toLowerCase();

  for (const alias of VEHICLE_ALIASES) {
    if (alias.from[0] === lowerMake && alias.from[1] === lowerModel) {
      [displayMake, displayModel] = alias.to;
      break;
    }
  }

  return {
    make: displayMake,
    model: displayModel,
    key: `${displayMake.toLowerCase()}|${displayModel.toLowerCase()}`,
  };
}

/**
 * Spec §3.3: a vehicle matches an entry when make and model are equal
 * ignoring case and from <= year <= to.
 * @param {FitmentEntry} entry
 * @param {Vehicle} vehicle
 * @returns {boolean}
 */
export function matches(entry, vehicle) {
  if (!entry || !vehicle) return false;
  const year = Number(vehicle.year);
  if (!Number.isInteger(year)) return false;
  if (!(entry.from <= year && year <= entry.to)) return false;
  return canon(entry.make, entry.model).key === canon(vehicle.make, vehicle.model).key;
}

/**
 * Every entry in the document that the vehicle matches.
 * @param {FitmentDoc|null} doc
 * @param {Vehicle} vehicle
 * @returns {FitmentEntry[]}
 */
export function matchingEntries(doc, vehicle) {
  if (!doc || !Array.isArray(doc.vehicles)) return [];
  return doc.vehicles.filter((entry) => matches(entry, vehicle));
}

/** SKUs join on trim().toUpperCase() on both sides. */
const skuKey = (sku) => clean(sku).toUpperCase();

/**
 * Whether a product payload can answer "does this fit?" at all. One that failed
 * to parse, or that lost its fitment document or its variants, cannot.
 * @param {*} data
 * @returns {boolean}
 */
export function usablePayload(data) {
  return Boolean(
    data && Array.isArray(data.variants) && data.fitment && Array.isArray(data.fitment.vehicles)
  );
}

/**
 * The variants that could fit this vehicle: those carrying a matching entry,
 * plus every universal variant. A SKU with no live variant is ignored; a
 * variant with a blank SKU is never a candidate.
 * @param {{fitment: FitmentDoc|null, variants: Variant[]}} data
 * @param {Vehicle} vehicle
 * @returns {Variant[]}
 */
export function candidates(data, vehicle) {
  if (!data || !Array.isArray(data.variants)) return [];
  const doc = data.fitment;
  const wanted = new Set();
  for (const entry of matchingEntries(doc, vehicle)) {
    for (const sku of entry.skus || []) wanted.add(skuKey(sku));
  }
  for (const sku of (doc && doc.universal) || []) wanted.add(skuKey(sku));

  return data.variants.filter((variant) => {
    const key = skuKey(variant.sku);
    return key !== '' && wanted.has(key);
  });
}

/**
 * The six things that can separate two parts that fit the same vehicle,
 * in the order they are asked. Spec §4.2.
 *
 * Truck details go last. They are conditions of fit, and the fit box states
 * the resolved part's conditions whether or not they were asked, so one left
 * unasked is still shown. Asked any earlier, the answer could settle what the
 * shopper never chose: on the SF cover "w/o Multi-Flex Tailgate" left only the
 * 6'6" part, so a 5'8" truck was told it fit, and on the canopy a Tundra's
 * truck details picked its door style.
 */
export const FACETS = [
  { key: 'cab', label: 'Cab size' },
  { key: 'box', label: 'Box size' },
  { key: 'style', label: 'Style' },
  { key: 'bulb', label: 'Bulb type' },
  { key: 'trim', label: 'Trim level' },
  { key: 'note', label: 'Truck details' },
];

/** Cab and truck details come from the fitment entries; the rest from the variant's own metafields. */
const ENTRY_FACETS = new Set(['cab', 'note']);

/**
 * The values this candidate offers for a facet, or null when it is a wildcard
 * (fits any value). A wildcard is either a missing attribute — "unknown", not
 * "none" — or an entry whose `cab` is [].
 *
 * @param {Variant} variant
 * @param {string} facetKey
 * @param {FitmentDoc|null} doc
 * @param {Vehicle} vehicle
 * @returns {Set<string>|null}
 */
export function facetValues(variant, facetKey, doc, vehicle) {
  if (ENTRY_FACETS.has(facetKey)) {
    const key = skuKey(variant.sku);
    const mine = matchingEntries(doc, vehicle).filter((entry) => (entry.skus || []).some((s) => skuKey(s) === key));

    // A universal variant appears in no entry: wildcard for cab and note.
    if (!mine.length) return null;

    if (facetKey === 'cab') {
      if (mine.some((entry) => !entry.cab || entry.cab.length === 0)) return null;
      return new Set(mine.flatMap((entry) => entry.cab));
    }
    return new Set(mine.map((entry) => clean(entry.note)));
  }

  const value = variant[facetKey];
  if (value == null) return null;
  if (Array.isArray(value)) return value.length ? new Set(value.map(clean)) : null;
  const text = clean(value);
  return text === '' ? null : new Set([text]);
}

/**
 * A facet's value sets for one kind of pool: the vehicle procedure reads
 * facetValues() against a vehicle, the parts procedure reads partFacetValues().
 * @typedef {(variant: Variant, facetKey: string) => (Set<string>|null)} ValuesOf
 */

/** @returns {ValuesOf} */
const vehicleValues = (doc, vehicle) => (variant, facetKey) => facetValues(variant, facetKey, doc, vehicle);

/** Concrete values across the pool, sorted for display. "" sorts first as "None of these". */
function optionsFor(pool, facetKey, valuesOf) {
  const values = new Set();
  for (const variant of pool) {
    const own = valuesOf(variant, facetKey);
    if (own) for (const value of own) values.add(value);
  }
  return [...values].sort((a, b) => {
    if (a === '') return -1;
    if (b === '') return 1;
    return a.localeCompare(b, undefined, { numeric: true });
  });
}

/**
 * Two candidates differ on this facet, counting a wildcard as "all values".
 * A signature is either JSON (always starts with "[") or the WILDCARD marker,
 * so the two can never collide.
 */
const WILDCARD = '*wildcard*';

function discriminates(pool, facetKey, valuesOf) {
  if (pool.length < 2) return false;
  let sawConcrete = false;
  const signatures = pool.map((variant) => {
    const own = valuesOf(variant, facetKey);
    if (!own) return WILDCARD;
    sawConcrete = true;
    return JSON.stringify([...own].sort());
  });
  // All-wildcard is not a question either.
  if (!sawConcrete) return false;
  return new Set(signatures).size > 1;
}

/**
 * The first facet that separates the pool, or null when one candidate is left
 * or nothing separates them (the caller then shows the SKU chooser, spec §4.2
 * step 6).
 *
 * @param {Variant[]} pool
 * @param {FitmentDoc|null} doc
 * @param {Vehicle} vehicle
 * @param {Set<string>} [skip] facet keys already answered
 * @returns {{key: string, label: string, options: string[]}|null}
 */
export function nextQuestion(pool, doc, vehicle, skip = new Set()) {
  return firstQuestion(pool, FACETS, vehicleValues(doc, vehicle), skip);
}

/**
 * @param {Array<string>} [always] facet keys asked whenever any candidate has a
 *   value for them, even with one candidate or one option. A question so asked
 *   with one option comes back `fixed`: narrowWith() answers it itself.
 */
function firstQuestion(pool, facets, valuesOf, skip, always = []) {
  if (!Array.isArray(pool) || !pool.length) return null;
  for (const facet of facets) {
    if (skip.has(facet.key)) continue;
    if (always.includes(facet.key)) {
      const options = optionsFor(pool, facet.key, valuesOf);
      if (!options.length) continue;
      return { key: facet.key, label: facet.label, options, fixed: options.length === 1 };
    }
    if (pool.length < 2) continue;
    if (!discriminates(pool, facet.key, valuesOf)) continue;
    const options = optionsFor(pool, facet.key, valuesOf);
    if (options.length < 2) continue;
    return { key: facet.key, label: facet.label, options };
  }
  return null;
}

/**
 * Keeps the candidates whose value set contains the answer, or that are
 * wildcards on that facet.
 *
 * @param {Variant[]} pool
 * @param {string} facetKey
 * @param {string} value
 * @param {FitmentDoc|null} doc
 * @param {Vehicle} vehicle
 * @returns {Variant[]}
 */
export function applyAnswer(pool, facetKey, value, doc, vehicle) {
  return keepAnswer(pool, facetKey, value, vehicleValues(doc, vehicle));
}

function keepAnswer(pool, facetKey, value, valuesOf) {
  return pool.filter((variant) => {
    const own = valuesOf(variant, facetKey);
    return own === null || own.has(value);
  });
}

/**
 * The whole §4.2 procedure: replays the answers given so far and returns what
 * is left, plus the question to ask next (null when one part is left or nothing
 * separates the rest — the caller then shows the SKU chooser).
 *
 * Each facet is asked at most once. An answer can leave the pool the same size
 * when parts' values overlap (one fits Crew Cab, another Crew and Extended) or
 * shrink it without settling that facet; either way the shopper has answered
 * it, and the next facet may still tell the rest apart. An answer that no
 * longer applies is dropped, and that question is asked again.
 *
 * A facet in `always` is asked in its place whenever any candidate has a value
 * for it, even when it separates nothing: canopies and box covers always show
 * Box size. With one option it is answered here, not left to the shopper.
 *
 * @param {{fitment: FitmentDoc|null, variants: Variant[]}} data
 * @param {Vehicle} vehicle
 * @param {Object<string, string>} answers facet key → the shopper's answer
 * @param {Array<string>} [always] facet keys always asked
 * @returns {{pool: Variant[], question: ({key: string, label: string, options: string[]}|null), asked: Array<{question: Object, answer: string}>}}
 */
export function narrowPool(data, vehicle, answers = {}, always = []) {
  const doc = data && data.fitment ? data.fitment : null;
  return narrowWith(candidates(data, vehicle), FACETS, vehicleValues(doc, vehicle), answers, always);
}

function narrowWith(start, facets, valuesOf, answers, always = []) {
  let pool = start;
  const asked = [];
  if (!pool.length) return { pool, question: null, asked };
  const answered = new Set();

  // Every pass consumes a distinct facet, so this ends within facets.length.
  for (;;) {
    const question = firstQuestion(pool, facets, valuesOf, answered, always);
    if (!question) break;
    const answer = question.fixed ? question.options[0] : answers[question.key];
    if (answer === undefined || !question.options.includes(answer)) return { pool, question, asked };
    asked.push({ question, answer });
    answered.add(question.key);
    pool = keepAnswer(pool, question.key, answer, valuesOf);
  }
  return { pool, question: null, asked };
}

/**
 * The answers a part already named (by a ?variant= link or a pick in "Browse
 * all fitments") gives to the questions, so they can be shown answered and the
 * shopper can see, and change, what was chosen. A question is answered only
 * when exactly one option keeps the part: where it fits either way, the
 * question is left for the shopper rather than answered for them.
 *
 * @param {{fitment: FitmentDoc|null, variants: Variant[]}} data
 * @param {Vehicle} vehicle
 * @param {Variant} variant
 * @param {Array<string>} [always] as for narrowPool()
 * @returns {Object<string, string>} facet key → answer, for narrowPool()
 */
export function answersFor(data, vehicle, variant, always = []) {
  const doc = data && data.fitment ? data.fitment : null;
  return answersWith((answers) => narrowPool(data, vehicle, answers, always), vehicleValues(doc, vehicle), variant);
}

function answersWith(narrow, valuesOf, variant) {
  const answers = {};
  // Every pass answers a new facet or stops, so this ends within the facet count.
  for (;;) {
    const { pool, question } = narrow(answers);
    if (!question || !variant || !pool.some((v) => v.id === variant.id)) return answers;
    const own = valuesOf(variant, question.key);
    const keeps = own ? question.options.filter((option) => own.has(option)) : [];
    if (keeps.length !== 1) return answers;
    answers[question.key] = keeps[0];
  }
}

/* ------------------------------------------ universal multi-part products -- */

/**
 * A universal product whose parts one dropdown cannot tell apart (the F1 LED
 * Light Kit sells two H11 parts; the FX kit an H11 with decoders and one
 * without) asks questions the way a vehicle product does, from the parts' own
 * attributes. Bulb type comes first: it is what a shopper knows about their
 * truck, and style splits what is left.
 */
export const PART_FACETS = [
  { key: 'bulb', label: 'Bulb type' },
  { key: 'style', label: 'Style' },
  { key: 'trim', label: 'Trim level' },
  { key: 'box', label: 'Box size' },
];

/**
 * What a part with no style is called beside parts that have one: the FX
 * kit's plain H11 beside its "w/ Decoders" H11 (Mike, 2026-09-29). Style only:
 * a missing bulb type or trim is still unknown, so still a wildcard. The block
 * repeats this word in Liquid.
 */
export const NO_STYLE = 'Standard';

/**
 * @param {Variant} variant
 * @param {string} facetKey
 * @returns {Set<string>|null}
 */
export function partFacetValues(variant, facetKey) {
  const own = facetValues(variant, facetKey, null, null);
  if (own === null && facetKey === 'style') return new Set([NO_STYLE]);
  return own;
}

/** Every part with a SKU. A universal product's parts all fit, so nothing else filters them. */
const partPool = (data) =>
  (data && Array.isArray(data.variants) ? data.variants : []).filter((variant) => skuKey(variant.sku) !== '');

/**
 * narrowPool() for a universal product: all its parts, PART_FACETS, no vehicle.
 * @param {{variants: Variant[]}} data
 * @param {Object<string, string>} [answers]
 */
export function narrowParts(data, answers = {}) {
  return narrowWith(partPool(data), PART_FACETS, partFacetValues, answers);
}

/**
 * answersFor() for a universal product: the answers a ?variant= part gives.
 * @param {{variants: Variant[]}} data
 * @param {Variant} variant
 * @returns {Object<string, string>}
 */
export function partAnswersFor(data, variant) {
  return answersWith((answers) => narrowParts(data, answers), partFacetValues, variant);
}

/**
 * Whether a universal product needs the questions: some answer to the first
 * one still leaves more than one part. Otherwise Horizon's own dropdown already
 * names each part once, and the questions would only repeat it.
 * blocks/eagle-vehicle-fit.liquid decides the same thing in Liquid, so the
 * first paint is right; this is the reference its tests pin.
 * @param {{variants: Variant[]}} data
 * @returns {boolean}
 */
export function partsPickerNeeded(data) {
  const pool = partPool(data);
  const question = firstQuestion(pool, PART_FACETS, partFacetValues, new Set());
  if (!question) return false;
  return question.options.some((option) => keepAnswer(pool, question.key, option, partFacetValues).length > 1);
}

/**
 * The conditions of fit to state for a resolved variant on this vehicle: its
 * cab values (unless wildcard) and its non-blank notes. Spec §4.2 step 7.
 *
 * @param {Variant} variant
 * @param {FitmentDoc|null} doc
 * @param {Vehicle} vehicle
 * @returns {{cab: string[], notes: string[]}}
 */
export function conditionsFor(variant, doc, vehicle) {
  const cabSet = facetValues(variant, 'cab', doc, vehicle);
  const noteSet = facetValues(variant, 'note', doc, vehicle);
  return {
    cab: cabSet ? [...cabSet].sort() : [],
    notes: noteSet ? simplifyNotes([...noteSet]) : [],
  };
}

/**
 * Every vehicle one part fits, one line each — "2015–2026 Ford F-150 (Crew
 * Cab) — note" — sorted by make, model and first year. The fit box lists them
 * for a part a search or link named, saved truck or not (Eagle, 2026-10-29).
 * A universal part, or one with no rows, gives none.
 *
 * @param {FitmentDoc|null} doc
 * @param {Variant|null} variant
 * @returns {string[]}
 */
export function partVehicles(doc, variant) {
  const key = variant ? skuKey(variant.sku) : '';
  if (!key) return [];
  const rows = ((doc && doc.vehicles) || [])
    .filter((entry) => (entry.skus || []).some((sku) => skuKey(sku) === key))
    .map((entry) => {
      const { make, model } = canon(entry.make, entry.model);
      const years = entry.from === entry.to ? `${entry.from}` : `${entry.from}–${entry.to}`;
      const cab = (entry.cab || []).length ? ` (${[...entry.cab].sort().join(', ')})` : '';
      const note = clean(entry.note) ? ` — ${clean(entry.note)}` : '';
      return { make, model, from: entry.from, text: `${years} ${make} ${model}${cab}${note}` };
    })
    .sort(
      (a, b) =>
        a.make.localeCompare(b.make) ||
        a.model.localeCompare(b.model, undefined, { numeric: true }) ||
        a.from - b.from
    );
  return [...new Set(rows.map((row) => row.text))];
}

/**
 * What a search that found nothing tries next, or null. Nearly every SKU
 * starts with "EA" and Shopify matches a SKU only from its first character, so
 * "735339-FS" misses EA735339-FS (Eagle, 2026-10-29). Only one word with a
 * digit in it, not already starting with EA, is retried; "2020 F-150" is not.
 *
 * @param {string} term
 * @returns {string|null}
 */
export function partNumberRetry(term) {
  const text = clean(term);
  if (!/\d/.test(text) || /\s/.test(text) || /^ea/i.test(text)) return null;
  return `EA${text}`;
}

/** How many products the header search lists as the shopper types. */
export const SUGGEST_LIMIT = 6;

/**
 * The header search's rows from Shopify's predictive search JSON
 * (/search/suggest.json, products only). A search that matched one part's SKU
 * comes back with that one variant: the row then links to it and shows its
 * own price, picture and description, and names the part when the query looks
 * like a part number.
 *
 * Fit labels need a saved vehicle and the catalogue index, and are product
 * level, so a part row never gets one: the canopy fitting a Sierra says
 * nothing about EA735339-FS, which does not. A universal product, or one the
 * index does not hold, gets none either, as on collection cards.
 *
 * @param {Array<Object>} products  resources.results.products
 * @param {{query?: string, vehicle?: ?Vehicle, items?: ?Array<IndexItem>}} [context]
 * @returns {Array<{url:string, title:string, part:?string, detail:?string, image:?string, price:string, was:?string, fit:?string, fitText:?string}>}
 */
export function suggestRows(products, { query = '', vehicle = null, items = null } = {}) {
  const byId = new Map((items || []).map((item) => [String(item.id), item]));
  const cents = (value) => Math.round(Number(value) * 100);
  const isPartQuery = /\d/.test(clean(query)) && !/\s/.test(clean(query));

  return (products || []).map((product) => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.length === 1 ? variants[0] : null;

    const price = variant ? cents(variant.price) : cents(product.price_min);
    const compare = variant ? cents(variant.compare_at_price) : cents(product.compare_at_price_max);
    const varies = !variant && cents(product.price_max) !== price;
    const image = (variant && variant.image) || product.image || null;

    let fit = null;
    let fitText = null;
    const item = byId.get(String(product.id));
    if (!variant && vehicle && item && !item.u) {
      fit = indexItemFits(item, vehicle) ? 'fits' : 'misfit';
      fitText = fit === 'fits' ? `Fits your ${vehicle.model}` : "Doesn't fit your truck";
    }

    return {
      url: variant && variant.url ? variant.url : product.url,
      title: clean(product.title),
      part: variant && isPartQuery ? clean(query).toUpperCase() : null,
      detail: variant ? clean(variant.title) || null : null,
      image: image ? `${image}${image.includes('?') ? '&' : '?'}width=96` : null,
      price: `${varies ? 'From ' : ''}${money(price)}`,
      was: !varies && compare > price ? money(compare) : null,
      fit,
      fitText,
    };
  });
}

/**
 * Products from Shopify's predictive search JSON for one query.
 * @param {string} base  routes.predictive_search_url
 * @param {string} q
 * @param {number} limit
 * @param {AbortSignal} [signal]
 * @returns {Promise<Array<Object>>}
 */
async function suggestProducts(base, q, limit, signal) {
  const params = new URLSearchParams({ q, 'resources[type]': 'product', 'resources[limit]': String(limit) });
  const response = await fetch(`${base}.json?${params}`, { signal });
  if (!response.ok) throw new Error(`predictive search failed: ${response.status}`);
  const data = await response.json();
  return (data && data.resources && data.resources.results && data.resources.results.products) || [];
}

/** The most "Did you mean…?" links the fit box lists; past this, its questions narrow faster. */
export const MAX_ALTERNATIVES = 4;

/** What makes one part like another: cab from its fitment rows, the rest from its own attributes. */
const LIKENESS_FACETS = ['cab', 'box', 'style', 'bulb', 'trim'];

/** Every cab the part's fitment rows name, for any vehicle, or null when none names one. */
function ownCabs(doc, variant) {
  const key = skuKey(variant.sku);
  const cabs = ((doc && doc.vehicles) || [])
    .filter((entry) => (entry.skus || []).some((sku) => skuKey(sku) === key))
    .flatMap((entry) => entry.cab || []);
  return cabs.length ? new Set(cabs) : null;
}

/**
 * "Did you mean…?" for a part that does not fit the vehicle: the parts of the
 * same product that do, those most like the named part first (the same cab,
 * box, style, bulb or trim), otherwise in the product's own order. A universal
 * part is offered only when nothing was made for the vehicle: it fits, but it
 * is rarely the part a shopper searched for.
 *
 * @param {{fitment: FitmentDoc|null, variants: Variant[]}} data
 * @param {Vehicle} vehicle
 * @param {Variant|null} named  the part that does not fit
 * @returns {Variant[]}
 */
export function alternativesFor(data, vehicle, named) {
  const pool = candidates(data, vehicle);
  const doc = data ? data.fitment : null;
  const universal = new Set(((doc && doc.universal) || []).map(skuKey));
  const specific = pool.filter((variant) => !universal.has(skuKey(variant.sku)));
  const offered = specific.length ? specific : pool;
  if (!named) return offered;

  const valuesOf = (variant, key) => (key === 'cab' ? ownCabs(doc, variant) : facetValues(variant, key, null, null));
  const likeness = (variant) =>
    LIKENESS_FACETS.filter((key) => {
      const mine = valuesOf(named, key);
      const theirs = valuesOf(variant, key);
      return Boolean(mine && theirs && [...mine].some((value) => theirs.has(value)));
    }).length;
  return offered
    .map((variant, index) => ({ variant, index, score: likeness(variant) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ variant }) => variant);
}

/**
 * How a "Did you mean…?" link names a part beside its SKU: what sets it apart
 * on this vehicle (its cab and truck details there), then its own box, style,
 * bulb and trim. Never the variant's title, which lists every truck the part
 * fits ("2001-2013 Silverado 1500 / 2001-2014 Silverado 2500 HD +6 more, …").
 *
 * @param {Variant} variant
 * @param {FitmentDoc|null} doc
 * @param {Vehicle} vehicle
 * @returns {string} "" when nothing sets it apart
 */
export function partSummary(variant, doc, vehicle) {
  const { cab, notes } = conditionsFor(variant, doc, vehicle);
  const own = ['box', 'style', 'bulb', 'trim'].flatMap((key) => [...(facetValues(variant, key, null, null) || [])]);
  return [...new Set([...cab, ...own, ...notes])].join(' · ');
}

/**
 * A note's conditions. AIMS's two fitment dropdowns arrive comma-joined in one
 * note (the sync's build of 2026-09-21), and the first can itself hold more
 * than one: "New Body, w/o RAMBOX, w/ Multifunction Tailgate".
 */
const noteParts = (note) => clean(note).split(',').map(clean).filter((part) => part !== '');

/** "w/ X" and "w/o X" are the two answers to one question; nothing else has an opposite. */
function opposite(part) {
  if (part.startsWith('w/ ')) return `w/o ${part.slice(3)}`;
  if (part.startsWith('w/o ')) return `w/ ${part.slice(4)}`;
  return null;
}

/** Two notes that differ only in "w/ X" against "w/o X" merge into what they share. */
function mergeOpposites(a, b) {
  if (a.length !== b.length) return null;
  const onlyA = a.filter((part) => !b.includes(part));
  const onlyB = b.filter((part) => !a.includes(part));
  if (onlyA.length !== 1 || onlyB.length !== 1 || opposite(onlyA[0]) !== onlyB[0]) return null;
  return a.filter((part) => part !== onlyA[0]);
}

/**
 * The notes a part fits under, stated as briefly as they stay true. A part
 * listed for both "w/o CarbonPro Bed, w/ MultiPro Tailgate" and "w/o CarbonPro
 * Bed, w/o MultiPro Tailgate" fits "w/o CarbonPro Bed": the tailgate doesn't
 * matter, so it isn't a condition. One listed for both "w/ Deck Rail System"
 * and "w/o Deck Rail System" has no condition at all. Only an exact w/ and w/o
 * pair is merged, so nothing is dropped that the notes don't prove irrelevant.
 * A blank note is not a condition, as before.
 *
 * @param {string[]} notes
 * @returns {string[]} sorted; empty when nothing limits the fit
 */
export function simplifyNotes(notes) {
  const byKey = new Map();
  for (const note of notes) {
    const parts = noteParts(note);
    if (!parts.length) continue;
    const key = [...parts].sort().join('\u001f');
    if (!byKey.has(key)) byKey.set(key, parts);
  }

  let terms = [...byKey.values()];
  for (let merged = true; merged; ) {
    merged = false;
    search: for (let i = 0; i < terms.length; i += 1) {
      for (let j = i + 1; j < terms.length; j += 1) {
        const shared = mergeOpposites(terms[i], terms[j]);
        if (!shared) continue;
        terms = terms.filter((_, k) => k !== i && k !== j).concat([shared]);
        merged = true;
        break search;
      }
    }
  }

  if (terms.some((parts) => parts.length === 0)) return [];
  // A note that asks for more than another adds nothing to what the other allows.
  const kept = terms.filter(
    (parts, i) => !terms.some((other, k) => k !== i && other.length < parts.length && other.every((p) => parts.includes(p)))
  );
  return [...new Set(kept.map((parts) => parts.join(', ')))].sort();
}

/**
 * What the truck-details question shows for each option. A condition every
 * option shares is not a choice, so it is left out: "New Body, w/o RAMBOX,
 * w/ Multifunction Tailgate" and "New Body, w/o RAMBOX, w/o Multifunction
 * Tailgate" read "w/ Multifunction Tailgate" and "w/o Multifunction Tailgate".
 * The fit box states the rest once the part resolves. Values are untouched.
 *
 * @param {string[]} options the question's option values
 * @returns {string[]} one text per option, in the same order
 */
export function noteOptionText(options) {
  const full = options.map((value) => (value === '' ? 'None of these' : value));
  // Beside "None of these", a shared condition is part of the choice.
  if (options.length < 2 || options.includes('')) return full;

  const parts = options.map(noteParts);
  const shared = parts[0].filter((part) => parts.every((list) => list.includes(part)));
  if (!shared.length) return full;

  const short = parts.map((list) => list.filter((part) => !shared.includes(part)).join(', '));
  if (short.includes('') || new Set(short).size !== short.length) return full;
  return short;
}

/* ---------------------------------------------------------------- garage -- */

export const GARAGE_KEY = 'eagle-garage-v1';

/** Set once at load; swapped in tests. Every access is wrapped in try/catch. */
let storage = (() => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch (error) {
    return null;
  }
})();

/** In-memory fallback for a page view where storage is blocked. */
let memoryVehicle = null;

/** @param {Storage|null} replacement */
export function __setStorageForTests(replacement) {
  storage = replacement;
  memoryVehicle = null;
}

const readRaw = () => {
  try {
    return storage ? storage.getItem(GARAGE_KEY) : null;
  } catch (error) {
    return null;
  }
};

/** Discards anything that is not a v1 record with an integer year and non-empty strings. */
function validate(record) {
  if (!record || typeof record !== 'object') return null;
  if (record.v !== 1) return null;
  const year = Number(record.year);
  if (!Number.isInteger(year)) return null;
  const make = clean(record.make);
  const model = clean(record.model);
  if (!make || !model) return null;
  return { year, make, model };
}

/**
 * The saved vehicle, or null.
 * @returns {Vehicle|null}
 */
export function getVehicle() {
  const raw = readRaw();
  if (raw) {
    try {
      const parsed = validate(JSON.parse(raw));
      if (parsed) return parsed;
    } catch (error) {
      /* fall through to the in-memory copy */
    }
  }
  return memoryVehicle ? { ...memoryVehicle } : null;
}

/** Notifies this page and, through the storage event, the others. */
function announce(vehicle) {
  if (typeof document === 'undefined' || !document.dispatchEvent) return;
  document.dispatchEvent(new CustomEvent('eagle:vehicle-change', { detail: { vehicle } }));
}

/**
 * Saves one vehicle. Make and model are canonicalised first, so the alias
 * table is applied once, on the way in.
 *
 * @param {Vehicle} vehicle
 * @returns {Vehicle|null} the stored vehicle, or null when it was incomplete.
 */
export function setVehicle(vehicle) {
  if (!vehicle) return null;
  const year = Number(vehicle.year);
  if (!Number.isInteger(year)) return null;
  const { make, model } = canon(vehicle.make, vehicle.model);
  if (!make || !model) return null;

  const stored = { year, make, model };
  memoryVehicle = stored;
  try {
    if (storage) storage.setItem(GARAGE_KEY, JSON.stringify({ v: 1, ...stored, savedAt: Date.now() }));
  } catch (error) {
    /* A functional preference, not data we must keep. The page view still works. */
  }
  announce({ ...stored });
  return { ...stored };
}

/** Forgets the vehicle. */
export function clearVehicle() {
  memoryVehicle = null;
  try {
    if (storage) storage.removeItem(GARAGE_KEY);
  } catch (error) {
    // removeItem can fail while getItem still works. Leaving the record in
    // place would let getVehicle() hand back the vehicle we just cleared, so
    // overwrite it with something validate() rejects.
    try {
      if (storage) storage.setItem(GARAGE_KEY, 'null');
    } catch (alsoFailed) {
      /* Storage is fully unavailable; the in-memory null above is enough. */
    }
  }
  announce(null);
}

/**
 * Mirrors Shopify: strip quotes, every other non-alphanumeric run becomes "-".
 *
 * Only quotes are stripped. "+" used to sit in this class, which turned "4+2"
 * into "42" where Shopify gives "4-2" \u2014 a silent 404 on a vehicle-tag URL, the
 * same failure the curly quotes caused before Task 4. Anything not listed here
 * falls through to the [^a-z0-9]+ rule below and becomes a hyphen.
 */
export function handleize(value) {
  return String(value)
    .toLowerCase()
    .replace(/['"\u2018\u2019\u201C\u201D]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * The collection tag handle for a vehicle, e.g. "vehicle-gmc-sierra-1500".
 * @param {Vehicle} vehicle
 * @returns {string}
 */
export function vehicleTagHandle(vehicle) {
  const { make, model } = canon(vehicle.make, vehicle.model);
  return handleize(`vehicle:${make} ${model}`);
}

/* ------------------------------------------------------- DOM: shared -- */

/** Everything is scoped to one section or quick-add dialog — a page can hold two product forms. */
const scopeOf = (element) => element.closest('.shopify-section, dialog') || document;

/**
 * Selects a variant by driving Horizon's own picker: set the native control,
 * dispatch `change`, and price, gallery, SKU, URL and add-to-cart follow.
 *
 * @param {ParentNode} scope
 * @param {number|string} variantId
 * @returns {boolean} whether a control was found and changed.
 */
export function selectVariant(scope, variantId) {
  const picker = scope.querySelector('variant-picker');
  if (!picker) return false;

  const option = picker.querySelector(`option[data-variant-id="${variantId}"]`);
  if (option) {
    const select = /** @type {HTMLSelectElement} */ (option.parentElement);
    if (select.value === option.value) return true; // already selected — do not thrash
    select.value = option.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  const radio = /** @type {HTMLInputElement|null} */ (picker.querySelector(`input[data-variant-id="${variantId}"]`));
  if (radio) {
    if (radio.checked) return true;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  return false;
}

/**
 * The variant id Horizon currently has selected in this scope.
 *
 * The select is read through selectedOptions, never through a selector list.
 * `querySelector('option:checked, option[selected]')` returns the first option
 * in TREE ORDER matching EITHER selector, and snippets/variant-main-picker.liquid
 * renders selected="selected" on the server-chosen option — an attribute that
 * never moves when the shopper changes the dropdown. Only :checked moves, so
 * after any client-side change two options matched and the stale server one won,
 * corrupting every state decision downstream. selectedOptions is unambiguous,
 * and it is populated for an untouched select too: the `selected` attribute sets
 * the option's initial selectedness, and a single-line select always has one.
 */
function selectedVariantId(scope) {
  const picker = scope.querySelector('variant-picker');
  if (!picker) return null;
  const select = picker.querySelector('select');
  const chosen = select && select.selectedOptions && select.selectedOptions[0];
  if (chosen && chosen.dataset.variantId) return Number(chosen.dataset.variantId);
  const radio = picker.querySelector('input:checked');
  if (radio && radio.dataset.variantId) return Number(radio.dataset.variantId);
  const hidden = scope.querySelector('input[name="id"]');
  return hidden && hidden.value ? Number(hidden.value) : null;
}

/**
 * Prices arrive in cents. The shop's currency comes from Shopify's own global
 * where it exists; CAD is this store's settlement currency and the fallback.
 * The module deliberately imports nothing, so Horizon's formatMoney is not
 * used here.
 *
 * Only the fallback for priceText(). The locale is pinned to en-CA: left to
 * the browser, an en-US shopper saw CAD written as "CA$", where Shopify and
 * the rest of the page write "$".
 */
const money = (cents) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: activeCurrency() || 'CAD' }).format(
    (Number(cents) || 0) / 100
  );

/** The shopper's currency from Shopify's own global, or null where there is none. */
function activeCurrency() {
  try {
    if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
      return Shopify.currency.active;
    }
  } catch (error) {
    /* Shopify global absent (tests, or a non-storefront context) */
  }
  return null;
}

/**
 * A variant's price as the storefront prints it. The fit block's payload
 * carries each price already run through Shopify's `money` filter, so this
 * matches Horizon's own price exactly and follows the shop's currency format
 * if it is ever changed in the admin.
 *
 * @param {{price:number, priceText?:string}} variant
 * @returns {string}
 */
export const priceText = (variant) =>
  typeof variant.priceText === 'string' ? variant.priceText : money(variant.price);

/**
 * The price a gated family leads with: its cheapest variant's, and that
 * variant's compare-at ("was") price when it is on sale. Among variants tied
 * for cheapest, one on sale wins, so a sale is never hidden behind a
 * full-price twin. The index's "cp" follows the same rule in Liquid
 * (templates/collection.fitment.liquid, snippets/eagle-rec-card.liquid).
 *
 * A missing price is unknown, not free, so such a variant is left out; with
 * none priced there is nothing to lead with and this returns null.
 *
 * @param {Array<{price:number, priceText?:string, compareText?:?string}>} variants
 * @returns {?{text:string, was:?string, varies:boolean}}
 */
export const leadPrice = (variants) => {
  // Number(null) is 0, so null is ruled out before the finite check.
  const priced = (variants || []).filter((v) => v.price != null && Number.isFinite(Number(v.price)));
  if (!priced.length) return null;
  const prices = priced.map((v) => Number(v.price));
  const min = Math.min(...prices);
  const cheapest = priced.filter((v) => Number(v.price) === min);
  const lead = cheapest.find((v) => typeof v.compareText === 'string') || cheapest[0];
  return {
    text: priceText(lead),
    was: typeof lead.compareText === 'string' ? lead.compareText : null,
    varies: Math.max(...prices) !== min,
  };
};

/**
 * Appends a struck-through "was" price to a price element, after the price
 * it is beside: the order the product page uses (sale price first). Struck
 * text is not announced by most screen readers, so a hidden label says what
 * the second figure is. Markup mirrored in snippets/eagle-rec-card.liquid.
 *
 * @param {HTMLElement} el
 * @param {?string} was
 * @param {string} label
 */
const appendWasPrice = (el, was, label) => {
  if (!was) return;
  const s = document.createElement('s');
  s.className = 'eagle-was-price';
  const hidden = document.createElement('span');
  hidden.className = 'visually-hidden';
  hidden.textContent = `${label} `;
  s.append(hidden, was);
  el.append(' ', s);
};

/**
 * Lets a re-render tell an unchanged control from one that must be rebuilt, so
 * the <select> a shopper is using is never destroyed underneath them.
 *
 * JSON rather than a join: an option value or a SKU may legitimately contain
 * whatever separator a join would pick, and two different lists must never
 * produce the same signature.
 */
const optionSignature = (options) => JSON.stringify(options);

const poolSignature = (pool) => JSON.stringify(pool.map((variant) => [variant.id, variant.sku, variant.label, variant.price]));

/* ------------------------------------------------ DOM: eagle-vehicle-fit -- */

// `HTMLElement` does not exist under plain Node, so a class literally
// `extends HTMLElement` throws at import time (the class body itself is
// never evaluated eagerly — only the extends expression is). Falling back to
// a bare class keeps this module importable by `node --test`; the
// `customElements` guard below still keeps the element from ever registering
// there. See assets/popover-polyfill.js for the same `typeof HTMLElement`
// check used for the same reason.
const HTMLElementBase = typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

class EagleVehicleFit extends HTMLElementBase {
  connectedCallback() {
    // Idempotent: the theme editor re-renders blocks, and state is always
    // derived from the garage and the selected variant, never kept here.
    this.mode = this.dataset.mode || 'none';
    this.scope = scopeOf(this);
    this.data = this.readData();
    // Facets asked even when they separate nothing: "box" on canopies and box covers.
    this.alwaysAsk = (this.dataset.alwaysAsk || '').split(',').map((key) => key.trim()).filter(Boolean);

    this.statusEl = this.querySelector('[data-role="status"]');
    this.summaryEl = this.querySelector('[data-role="summary"]');
    this.partVehiclesEl = this.querySelector('[data-role="part-vehicles"]');
    this.conditionsEl = this.querySelector('[data-role="conditions"]');
    this.suggestEl = this.querySelector('[data-role="suggestions"]');
    this.actionsEl = this.querySelector('[data-role="actions"]');
    this.helperEl = this.querySelector('[data-role="helper"]');
    this.followupsEl = this.querySelector('[data-role="followups"]');
    this.chooserEl = this.querySelector('[data-role="chooser"]');
    this.selects = {
      year: this.querySelector('[data-role="year"]'),
      make: this.querySelector('[data-role="make"]'),
      model: this.querySelector('[data-role="model"]'),
    };
    this.labels = {
      year: this.selects.year ? this.selects.year.options[0].textContent : 'Year',
      make: this.selects.make ? this.selects.make.options[0].textContent : 'Make',
      model: this.selects.model ? this.selects.model.options[0].textContent : 'Model',
    };
    this.hints = { year: '', make: 'choose a year first', model: 'choose a make first' };

    // A payload that will not parse, or that lost its fitment document, cannot
    // answer "does this fit?". Left in a vehicle mode, the box would hide
    // Horizon's variant picker (the block's stylesheet does that in those
    // modes) and hold Add to cart shut behind a Year list with nothing in it.
    // Step aside instead: Horizon's own picker comes back, the button is never
    // gated, and the status says what it says for a product with no data.
    if ((this.mode === 'vehicle' || this.mode === 'vehicle-single') && !usablePayload(this.data)) {
      console.warn('[eagle-fitment] the product payload is unusable; falling back to the store\'s own picker');
      this.mode = 'none';
      this.dataset.mode = 'none';
      const selects = this.querySelector('[data-role="vehicle-selects"]');
      if (selects) selects.hidden = true;
      this.setStatus('i', this.dataset.emptyText || 'See the product description for fitment details.');
    }

    // The same step aside for a universal product's questions: Horizon's own
    // dropdown still names every part, and nothing is gated.
    if (this.mode === 'parts' && !usablePayload(this.data)) {
      console.warn('[eagle-fitment] the product payload is unusable; falling back to the store\'s own picker');
      this.mode = 'universal';
      this.dataset.mode = 'universal';
      const selects = this.querySelector('[data-role="part-selects"]');
      if (selects) selects.hidden = true;
    }

    if (this.data && this.data.fitment && this.data.fitment.version !== 1) {
      console.warn('[eagle-fitment] unknown custom.fitment version', this.data.fitment.version);
    }

    this.answers = {};
    this.resolvedByPicker = false;
    // The id this element last asked Horizon to select, so the round trip it
    // causes is not mistaken for the shopper's own pick. Keyed on the id rather
    // than a boolean: a boolean would poison the next genuine pick if the event
    // never arrived, whereas a stale id simply fails to match and self-corrects.
    this.autoSelectedId = null;

    if (this.mode === 'vehicle') this.wireSelects();
    this.wireActions();

    this.onVehicleChange = () => {
      this.syncSelectsToGarage();
      this.render();
    };
    this.onProductSelect = (event) => {
      // selectVariant() dispatches a native change, Horizon turns that into
      // shopify:product:select, and this listener hears it. Without this check
      // the module's own resolution marked the page "chosen by hand", so a later
      // vehicle change showed state C ("does not fit") with add-to-cart enabled
      // instead of state B2 with the gate on.
      const settled = selectedVariantId(this.scope);
      const mine = this.autoSelectedId !== null && settled === this.autoSelectedId;
      this.autoSelectedId = null;
      if (!mine) this.resolvedByPicker = true;
      this.render();

      // This listener runs synchronously as the event is dispatched, before the
      // section fetch behind it has even been issued. Horizon acts on the far
      // side of that fetch: product-form.js awaits the promise and only then
      // re-enables add-to-cart, morphs the button from server HTML (taking
      // aria-describedby with it) and un-hides accelerated checkout. A gate
      // asserted here is wiped by those later writes and nothing re-applies it,
      // so changing an earlier answer while a fetch was in flight left an
      // unanswered question, a "From $X" price and a working Add to cart for
      // the part the shopper had just revoked — the one outcome the gate
      // exists to prevent. Re-assert once the round trip lands. Every branch of
      // render() is idempotent, so the second pass costs nothing when the state
      // has not moved, and a rejected promise (an aborted fetch) is not ours to
      // report.
      const settle = event && event.promise;
      if (settle && typeof settle.then === 'function') settle.then(() => this.render(), () => {});
    };
    this.onPageShow = () => {
      this.syncSelectsToGarage();
      this.render();
    };

    document.addEventListener('eagle:vehicle-change', this.onVehicleChange);
    window.addEventListener('storage', this.onVehicleChange);
    window.addEventListener('pageshow', this.onPageShow);
    this.scope.addEventListener('shopify:product:select', this.onProductSelect);

    if (this.mode === 'vehicle') this.fillYears();
    this.prefillFromGarage();
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('eagle:vehicle-change', this.onVehicleChange);
    window.removeEventListener('storage', this.onVehicleChange);
    window.removeEventListener('pageshow', this.onPageShow);
    this.scope.removeEventListener('shopify:product:select', this.onProductSelect);
  }

  readData() {
    const node = this.querySelector('script[data-role="fitment-data"]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[eagle-fitment] could not parse the product payload', error);
      return null;
    }
  }

  get doc() {
    return this.data && this.data.fitment ? this.data.fitment : null;
  }

  /* ------------------------------------------------------------ selects -- */

  wireSelects() {
    this.selects.year.addEventListener('change', () => {
      this.resetSelect('make');
      this.resetSelect('model');
      this.clearFollowups();
      this.fillMakes();
      this.commitOrRender();
    });
    this.selects.make.addEventListener('change', () => {
      this.resetSelect('model');
      this.clearFollowups();
      this.fillModels();
      this.commitOrRender();
    });
    this.selects.model.addEventListener('change', () => {
      this.clearFollowups();
      this.commitOrRender();
    });
  }

  /**
   * Saves the vehicle as soon as the three selects agree, and renders when they
   * do not.
   *
   * fill() answers a single-option list on the shopper's behalf by assigning
   * select.value, which fires no change event, and only the model select's own
   * handler used to save. So a year whose make and model each held one option
   * left all three selects visibly answered while the status still read
   * "Select your vehicle to check fit.", add-to-cart stayed gated, and there
   * was no way forward: re-picking the only option fires nothing either. Live
   * on 3 of the 19 fitment products — 8 of the 11 years on Mud Flap Brackets,
   * 2004-2008 on the Tacoma tonneau cover, 2023-2026 on the Silverado fender
   * flares.
   *
   * Called only from the change handlers, never from connectedCallback's
   * fillYears(), so a vehicle is still never saved without the shopper acting.
   */
  commitOrRender() {
    const year = Number(this.selects.year.value);
    const make = this.selects.make.value;
    const model = this.selects.model.value;
    if (year && make && model) setVehicle({ year, make, model });
    else this.render();
  }

  /** Every year any entry covers, newest first. Built from this product, so no choice dead-ends. */
  fillYears() {
    const years = new Set();
    for (const entry of (this.doc && this.doc.vehicles) || []) {
      for (let y = entry.from; y <= entry.to; y += 1) years.add(y);
    }
    this.fill('year', [...years].sort((a, b) => b - a).map(String));
  }

  fillMakes() {
    const year = Number(this.selects.year.value);
    if (!year) return;
    const makes = new Map();
    for (const entry of (this.doc && this.doc.vehicles) || []) {
      if (entry.from <= year && year <= entry.to) {
        const { make } = canon(entry.make, entry.model);
        makes.set(make.toLowerCase(), make);
      }
    }
    this.fill('make', [...makes.values()].sort((a, b) => a.localeCompare(b)));
  }

  fillModels() {
    const year = Number(this.selects.year.value);
    const make = this.selects.make.value;
    if (!year || !make) return;
    const models = new Map();
    for (const entry of (this.doc && this.doc.vehicles) || []) {
      if (!(entry.from <= year && year <= entry.to)) continue;
      const c = canon(entry.make, entry.model);
      if (c.make.toLowerCase() !== make.toLowerCase()) continue;
      models.set(c.model.toLowerCase(), c.model);
    }
    this.fill('model', [...models.values()].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
  }

  /**
   * A list with exactly one option is selected automatically.
   *
   * That auto-selection is not announced. This element has no announcer of its
   * own (EagleFitmentBar's [data-role="announcer"] has no counterpart here), and
   * the role="status" line carries the fit result, not picker changes. A screen
   * reader user meets the pre-filled value when they reach the select.
   */
  fill(kind, values) {
    const select = this.selects[kind];
    if (!select) return;
    select.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.labels[kind];
    select.append(placeholder);
    for (const value of values) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.append(option);
    }
    this.setAvailability(kind, values.length > 0);
    if (values.length === 1) {
      select.value = values[0];
      if (kind === 'year') this.fillMakes();
      if (kind === 'make') this.fillModels();
    }
  }

  resetSelect(kind) {
    const select = this.selects[kind];
    if (!select) return;
    select.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.labels[kind];
    select.append(placeholder);
    this.setAvailability(kind, false);
  }

  setAvailability(kind, available) {
    const select = this.selects[kind];
    if (!select) return;
    select.setAttribute('aria-disabled', available ? 'false' : 'true');
    const hint = this.hints[kind];
    select.setAttribute('aria-label', available || !hint ? this.labels[kind] : `${this.labels[kind]}, ${hint}`);
  }

  prefillFromGarage() {
    const vehicle = getVehicle();
    if (!vehicle || this.mode !== 'vehicle') return;
    const { year, make, model } = vehicle;
    if (!this.selects.year) return;
    const hasYear = [...this.selects.year.options].some((o) => o.value === String(year));
    if (!hasYear) return;
    this.selects.year.value = String(year);
    this.fillMakes();
    const hasMake = [...this.selects.make.options].some((o) => o.value.toLowerCase() === make.toLowerCase());
    if (!hasMake) return;
    this.selects.make.value = [...this.selects.make.options].find((o) => o.value.toLowerCase() === make.toLowerCase()).value;
    this.fillModels();
    const modelOption = [...this.selects.model.options].find((o) => o.value.toLowerCase() === model.toLowerCase());
    if (modelOption) this.selects.model.value = modelOption.value;
  }

  /**
   * Re-reads the selects from the saved vehicle when it was changed somewhere
   * other than these selects: the header's vehicle button, another tab, or a
   * Back navigation that restored this page from the bfcache. Without this the
   * status line named the new truck while the selects still showed the old one.
   *
   * A shopper's half-made pick (no vehicle saved, selects incomplete) is left
   * alone, and selects that already agree are not rebuilt.
   */
  syncSelectsToGarage() {
    if (this.mode !== 'vehicle' || !this.selects.year) return;
    const vehicle = getVehicle();
    const year = Number(this.selects.year.value);
    const make = this.selects.make.value.toLowerCase();
    const model = this.selects.model.value.toLowerCase();
    if (vehicle) {
      if (year === vehicle.year && make === vehicle.make.toLowerCase() && model === vehicle.model.toLowerCase()) return;
    } else if (!(year && make && model)) {
      return;
    }
    this.resetSelect('make');
    this.resetSelect('model');
    this.clearFollowups();
    this.fillYears();
    this.prefillFromGarage();
  }

  /* --------------------------------------------------------- follow-ups -- */

  clearFollowups() {
    this.answers = {};
    if (this.followupsEl) this.followupsEl.innerHTML = '';
    if (this.chooserEl) {
      this.chooserEl.innerHTML = '';
      this.chooserEl.hidden = true;
    }
  }

  /**
   * Runs the §4.2 procedure over the answers given so far. Every answered
   * question stays in `asked`, so renderFollowups() keeps the <select> the
   * shopper just used — and its focus — in place.
   */
  narrow(vehicle) {
    return narrowPool(this.data, vehicle, this.answers, this.alwaysAsk);
  }

  /**
   * Idempotent, the same way setCard() is in EagleCollectionFit: a field is
   * rebuilt only when its facet key or its option list actually changed.
   *
   * This runs from render(), which runs from a facet <select>'s own change
   * handler. Wiping and rebuilding destroyed the control the shopper was using
   * mid-dispatch and dropped focus to <body> on every answer. Spec §6.2: do not
   * move focus when a question appears or when a variant resolves.
   */
  renderFollowups(asked, question) {
    if (!this.followupsEl) return;
    const steps = [...asked, ...(question ? [{ question, answer: '' }] : [])];

    for (let index = 0; index < steps.length; index += 1) {
      const { question: facet, answer } = steps[index];
      const existing = this.followupsEl.children[index];
      const select = existing ? existing.querySelector('select') : null;

      if (select && select.dataset.facet === facet.key && select.dataset.options === optionSignature(facet.options)) {
        // Same question, same choices: leave the control — and the focus and
        // the open dropdown that may be on it — exactly where they are.
        const value = answer || '';
        if (select.value !== value) select.value = value;
        continue;
      }

      const field = this.buildFacetField(facet, answer);
      if (existing) existing.replaceWith(field);
      else this.followupsEl.append(field);
    }

    while (this.followupsEl.children.length > steps.length) {
      this.followupsEl.lastElementChild.remove();
    }
  }

  buildFacetField(question, answer) {
    const wrapper = document.createElement('p');
    wrapper.className = 'eagle-fit-box__field';
    const id = `${this.dataset.blockId}-facet-${question.key}`;

    const label = document.createElement('label');
    label.setAttribute('for', id);
    label.textContent = question.label;

    const select = document.createElement('select');
    select.id = id;
    select.dataset.facet = question.key;
    // What renderFollowups() compares against to decide whether to reuse this
    // field. It lives on the element, not on the element's container, so
    // clearFollowups()'s innerHTML wipe takes the signature with it.
    select.dataset.options = optionSignature(question.options);

    // A question answered for the shopper (one box size on a canopy) has no
    // blank to go back to: it would only snap back to the one size.
    if (!question.fixed) {
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = question.label;
      select.append(placeholder);
    }

    // A blank note is a real answer: the row names no condition.
    const texts = question.key === 'note'
      ? noteOptionText(question.options)
      : question.options.map((value) => (value === '' ? 'None of these' : value));
    question.options.forEach((value, index) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = texts[index];
      select.append(option);
    });
    if (answer) select.value = answer;

    select.addEventListener('change', () => {
      // Changing an earlier answer discards the later ones.
      const order = (this.mode === 'parts' ? PART_FACETS : FACETS).map((f) => f.key);
      const cut = order.indexOf(question.key);
      for (const key of order.slice(cut)) delete this.answers[key];
      if (select.value) this.answers[question.key] = select.value;
      // An answer is the shopper choosing: the part a ?variant= link or the
      // native picker named no longer decides, the same as "Show parts that fit".
      this.resolvedByPicker = false;
      this.dataset.variantExplicit = 'false';
      this.render();
    });

    wrapper.append(label, select);
    return wrapper;
  }

  /**
   * Spec §4.2 step 6: nothing separates them, so name them by SKU.
   *
   * Idempotent for the same reason renderFollowups() is: choosing a part here
   * drives Horizon's picker, which comes back through shopify:product:select
   * and re-renders this very chooser. Rebuilding it would destroy the select
   * the shopper just used and drop focus to <body>.
   */
  renderChooser(pool) {
    if (!this.chooserEl) return;
    const signature = poolSignature(pool);
    const current = this.chooserEl.querySelector('select');
    if (current && current.dataset.pool === signature) {
      this.chooserEl.hidden = false;
      return;
    }

    this.chooserEl.innerHTML = '';
    this.chooserEl.hidden = false;

    const help = document.createElement('p');
    help.className = 'eagle-fit-box__chooser-help';
    const helpText = this.mode === 'parts'
      ? this.dataset.partsChooserHelp || 'More than one part matches. Not sure which?'
      : this.dataset.chooserHelp || 'More than one part matches your truck. Not sure which?';
    help.textContent = `${helpText} `;
    const link = document.createElement('a');
    link.href = this.dataset.dealersUrl || '/pages/our-dealers';
    link.textContent = 'Ask a dealer';
    help.append(link);

    const wrapper = document.createElement('p');
    wrapper.className = 'eagle-fit-box__field';
    const id = `${this.dataset.blockId}-chooser`;
    const label = document.createElement('label');
    label.setAttribute('for', id);
    label.textContent = this.dataset.chooserLabel || 'Choose your part';

    const select = document.createElement('select');
    select.id = id;
    select.dataset.pool = signature;
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.dataset.chooserLabel || 'Choose your part';
    select.append(placeholder);
    for (const variant of pool) {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent = `${variant.sku} — ${variant.label} — ${priceText(variant)}`;
      select.append(option);
    }
    select.addEventListener('change', () => {
      if (select.value) selectVariant(this.scope, select.value);
    });

    wrapper.append(label, select);
    this.chooserEl.append(help, wrapper);
  }

  /**
   * State C's "Did you mean…?": a link to each part of this product that fits
   * the saved vehicle. Past MAX_ALTERNATIVES the list stays hidden and "Show
   * parts that fit" does the choosing; its questions narrow faster than a long
   * list reads.
   *
   * Each is a real link to ?variant=, so it can open in a new tab like any
   * other. A plain click drives Horizon's picker instead of reloading, and the
   * pick comes back through onProductSelect as the shopper's own, so the box
   * moves to B, "Fits your …".
   *
   * Idempotent like renderChooser(): render() runs on every product-select
   * round trip and vehicle change, and rebuilding would drop the focus of a
   * shopper tabbing through the links.
   *
   * @param {Variant[]} list
   */
  renderAlternatives(list) {
    if (!this.suggestEl) return;
    if (!list.length || list.length > MAX_ALTERNATIVES) {
      this.suggestEl.hidden = true;
      return;
    }
    this.suggestEl.hidden = false;
    const signature = poolSignature(list);
    if (this.suggestEl.dataset.pool === signature) return;
    this.suggestEl.dataset.pool = signature;
    this.suggestEl.innerHTML = '';

    const vehicle = getVehicle();
    const lead = document.createElement('p');
    lead.className = 'eagle-fit-box__suggest-lead';
    lead.textContent = list.length === 1
      ? this.dataset.suggestOneText || 'Did you mean to add this part?'
      : this.dataset.suggestManyText || 'Did you mean to add one of these?';

    const items = document.createElement('ul');
    items.className = 'eagle-fit-box__suggest-list';
    const base = this.dataset.productUrl || window.location.pathname;
    for (const variant of list) {
      const link = document.createElement('a');
      link.href = `${base}?variant=${variant.id}`;
      const sku = document.createElement('strong');
      sku.textContent = variant.sku;
      const summary = vehicle ? partSummary(variant, this.doc, vehicle) : '';
      link.append(sku, document.createTextNode(` — ${summary ? `${summary} — ` : ''}${priceText(variant)}`));
      link.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (!selectVariant(this.scope, variant.id)) return; // no picker here: let the link load the page
        event.preventDefault();
        // The link is about to disappear with the list. Keep the keyboard in
        // the box, on the line that now says the part fits.
        if (this.statusEl) {
          this.statusEl.tabIndex = -1;
          this.statusEl.focus();
        }
      });
      const item = document.createElement('li');
      item.append(link);
      items.append(item);
    }
    this.suggestEl.append(lead, items);
  }

  /* ------------------------------------------------------------- render -- */

  wireActions() {
    const change = this.querySelector('[data-role="change-vehicle"]');
    const showFitting = this.querySelector('[data-role="show-fitting"]');
    if (change) {
      change.addEventListener('click', (event) => {
        if (this.mode === 'vehicle' && this.selects.year) {
          // No propagation to stop here: this branch opens nothing, and letting
          // the click reach document keeps the header panel and any <details>
          // that auto-closes, closing on an outside click exactly as they do today.
          this.selects.year.focus();
        } else {
          // The bar's own click-away handler lives on document. Without this,
          // the click that calls open() carries straight on to that handler,
          // which sees a target in neither the panel nor the trigger and closes
          // the panel again inside the same event — leaving focus on <body>,
          // because open() had already moved it into the now-hidden panel.
          event.stopPropagation();
          const bar = document.querySelector('eagle-fitment-bar');
          if (bar && typeof bar.open === 'function') bar.open();
        }
      });
    }
    if (showFitting) {
      showFitting.addEventListener('click', () => {
        this.answers = {};
        this.resolvedByPicker = false;
        // Also drop the ?variant= claim. The shopper has explicitly asked to
        // stop treating the URL's part as their choice; leaving this set would
        // send every later render back through the explicit-choice branches
        // and they could never reach the questions again.
        this.dataset.variantExplicit = 'false';
        this.render();
        if (this.selects.year) this.selects.year.focus();
      });
    }
  }

  setStatus(icon, text) {
    if (!this.statusEl) return;
    this.statusEl.innerHTML = '';
    const glyph = document.createElement('span');
    glyph.className = 'eagle-fit-box__icon';
    glyph.setAttribute('aria-hidden', 'true');
    glyph.textContent = icon;
    this.statusEl.append(glyph, document.createTextNode(` ${text}`));
  }

  setConditions(conditions) {
    if (!this.conditionsEl) return;
    const parts = [...conditions.cab, ...conditions.notes];
    if (!parts.length) {
      this.conditionsEl.hidden = true;
      this.conditionsEl.textContent = '';
      return;
    }
    this.conditionsEl.hidden = false;
    this.conditionsEl.textContent = `Fits: ${parts.join(' · ')}`;
  }

  showActions({ change = false, showFitting = false, seeWhatFits = null }) {
    if (!this.actionsEl) return;
    this.actionsEl.hidden = !(change || showFitting || seeWhatFits);
    const changeEl = this.querySelector('[data-role="change-vehicle"]');
    const fittingEl = this.querySelector('[data-role="show-fitting"]');
    const seeEl = this.querySelector('[data-role="see-what-fits"]');
    if (changeEl) changeEl.hidden = !change;
    if (fittingEl) fittingEl.hidden = !showFitting;
    if (seeEl) {
      seeEl.hidden = !seeWhatFits;
      if (seeWhatFits) seeEl.href = seeWhatFits;
    }
  }

  /**
   * States C and D — the part does not fit — are drawn in red, and C offers the
   * parts that do. Both are reset on every pass and set only by the branch that
   * calls for them, so no other state can inherit them.
   */
  render() {
    this.misfit = false;
    this.alternatives = [];
    this.renderState();
    this.toggleAttribute('data-misfit', this.misfit);
    this.renderAlternatives(this.alternatives);
    this.renderPartVehicles();
  }

  /**
   * The vehicles a named part fits, in place of the product-wide "Fits 25
   * truck models" summary: a part-number search or a ?variant= link names one
   * part, and someone who knows the number wants to see what it goes on
   * (Eagle, 2026-10-29). Rebuilt only when the list changes, so the role="status"
   * line stays the only thing announced.
   */
  renderPartVehicles() {
    const el = this.partVehiclesEl;
    if (!el || !this.data) return;
    const currentId = selectedVariantId(this.scope);
    const variant = this.explicitlyChosen() ? (this.data.variants || []).find((v) => v.id === currentId) : null;
    const lines = partVehicles(this.doc, variant || null);
    if (this.summaryEl) this.summaryEl.hidden = lines.length > 0;
    el.hidden = !lines.length;

    const signature = lines.length ? JSON.stringify([variant.sku, lines]) : '';
    if (el.dataset.signature === signature) return;
    el.dataset.signature = signature;
    if (!lines.length) {
      el.replaceChildren();
      return;
    }
    const heading = document.createElement('p');
    heading.className = 'eagle-fit-box__part-vehicles-heading';
    heading.textContent = `Part ${variant.sku} fits:`;
    const list = document.createElement('ul');
    list.className = 'eagle-fit-box__part-vehicles-list';
    for (const line of lines) {
      const item = document.createElement('li');
      item.textContent = line;
      list.append(item);
    }
    el.replaceChildren(heading, list);
  }

  renderState() {
    const vehicle = getVehicle();
    const doc = this.doc;

    // F · no data, and E · universal product.
    if (this.mode === 'none') return this.applyGate(false);
    if (this.mode === 'parts') return this.renderParts();
    if (this.mode === 'universal') {
      this.showActions({});
      return this.applyGate(false);
    }

    // A · idle.
    if (!vehicle) {
      this.setStatus('?', this.dataset.idleText || 'Select your vehicle to check fit.');
      this.setConditions({ cab: [], notes: [] });
      this.showActions({ change: this.mode === 'vehicle-single' });
      this.clearFollowups();
      return this.applyGate(this.mode === 'vehicle' && !this.explicitlyChosen());
    }

    const label = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    const pool = candidates(this.data, vehicle);

    // D · no fit.
    if (!pool.length) {
      this.misfit = true;
      this.setStatus('✕', `Does not fit your ${label}.`);
      this.setConditions({ cab: [], notes: [] });
      this.showActions({ change: true, seeWhatFits: `/collections/all/${vehicleTagHandle(vehicle)}` });
      this.clearFollowups();
      return this.applyGate(this.mode === 'vehicle' && !this.explicitlyChosen());
    }

    const currentId = selectedVariantId(this.scope);
    const current = pool.find((v) => v.id === currentId);

    // E · the selected variant is a universal part. Checked before the pool
    // logic, because candidates() folds universal SKUs into every pool, so
    // pool membership can never distinguish this case.
    const selectedVariant = (this.data.variants || []).find((v) => v.id === currentId);
    if (selectedVariant && doc) {
      const universal = new Set((doc.universal || []).map((s) => String(s).trim().toUpperCase()));
      if (universal.has(String(selectedVariant.sku).trim().toUpperCase())) {
        this.setStatus('✓', this.dataset.universalText || 'Universal fit — fits any truck.');
        this.setConditions({ cab: [], notes: [] });
        this.showActions({ change: true });
        return this.applyGate(false);
      }
    }

    // C · a part that does not fit was chosen by hand or reached by ?variant= —
    // which is where a part-number search lands. Warn, offer the parts that do
    // fit, and leave Add to cart alone: dealers and fleets order for trucks
    // other than the one saved (Eagle, 2026-10-28).
    if (this.resolvedByPicker || this.dataset.variantExplicit === 'true') {
      if (currentId && !current) {
        const selected = (this.data.variants || []).find((v) => v.id === currentId);
        this.misfit = true;
        this.alternatives = alternativesFor(this.data, vehicle, selected || null);
        this.setStatus('!', `This part (${selected ? selected.sku : 'selected'}) does not fit your ${label}.`);
        this.setConditions({ cab: [], notes: [] });
        this.showActions({ change: true, showFitting: true });
        return this.applyGate(false);
      }
    }

    // B · an explicit choice that DOES fit. A ?variant= link or a manual pick
    // has already named a part, so say so rather than re-asking a question the
    // shopper has effectively answered. `this.answers` does not survive a
    // reload, so without this a shared or bookmarked resolved URL came back as
    // B2 ("4 parts fit, choose box size") with the right part already selected
    // and buyable — a contradiction the shopper has no way to resolve.
    //
    // The questions still show, answered the way the named part answers them,
    // so the shopper can see what was chosen and change it: a link to the
    // 6'4" cover without the Multifunction Tailgate offers the tailgate
    // question too. Answering one hands the choice back to the questions
    // (buildFacetField).
    if (this.explicitlyChosen() && current) {
      this.answers = answersFor(this.data, vehicle, current, this.alwaysAsk);
      const { question: open, asked: shown = [] } = this.narrow(vehicle);
      this.renderFollowups(shown, open);
      if (this.chooserEl) this.chooserEl.hidden = true;
      this.setStatus('✓', `Fits your ${label}.`);
      this.setConditions(conditionsFor(current, doc, vehicle));
      this.showActions({ change: true });
      return this.applyGate(false);
    }

    if (this.mode === 'vehicle-single') {
      this.setStatus('✓', `Fits your ${label}.`);
      this.setConditions(conditionsFor(pool[0], doc, vehicle));
      this.showActions({ change: true });
      return this.applyGate(false);
    }

    const { pool: narrowed, question, asked = [] } = this.narrow(vehicle);

    // B2 · choose.
    if (question) {
      this.setStatus('?', `${narrowed.length} parts fit your ${label}. Choose ${question.label.toLowerCase()}.`);
      this.setConditions({ cab: [], notes: [] });
      this.renderFollowups(asked, question);
      if (this.chooserEl) this.chooserEl.hidden = true;
      this.showActions({ change: true });
      return this.applyGate(!this.explicitlyChosen());
    }

    // Nothing separates them: the SKU chooser.
    if (narrowed.length > 1) {
      this.setStatus('?', `${narrowed.length} parts fit your ${label}. Choose your part.`);
      this.setConditions({ cab: [], notes: [] });
      this.renderFollowups(asked, null);
      this.renderChooser(narrowed);
      this.showActions({ change: true });
      return this.applyGate(!this.explicitlyChosen());
    }

    // B · fits, resolved.
    const resolved = narrowed[0];
    this.renderFollowups(asked, null);
    if (this.chooserEl) this.chooserEl.hidden = true;
    this.setStatus('✓', `Fits your ${label}.`);
    this.setConditions(conditionsFor(resolved, doc, vehicle));
    this.showActions({ change: true });
    if (resolved.id !== currentId) {
      // Claim the round trip before it starts: the change event this causes
      // comes back through onProductSelect, which must not read it as a
      // shopper's pick.
      this.autoSelectedId = resolved.id;
      selectVariant(this.scope, resolved.id);
    }
    return this.applyGate(false);
  }

  /**
   * E, with questions: a universal product whose parts one dropdown cannot
   * tell apart. The same steps as B2 → B, over every part and PART_FACETS; the
   * saved vehicle plays no part, and the status keeps its server-rendered
   * "Universal fit" line.
   */
  renderParts() {
    this.setConditions({ cab: [], notes: [] });
    this.showActions({});
    const currentId = selectedVariantId(this.scope);
    const current = (this.data.variants || []).find((v) => v.id === currentId);

    // A ?variant= link or a pick in "Browse all parts" named the part: show
    // the questions answered the way it answers them, and leave the choice.
    if (this.explicitlyChosen() && current) {
      this.answers = partAnswersFor(this.data, current);
      const { pool, question, asked } = narrowParts(this.data, this.answers);
      this.renderFollowups(asked, question);
      if (!question && pool.length > 1) {
        this.renderChooser(pool);
        const chooser = this.chooserEl && this.chooserEl.querySelector('select');
        if (chooser && pool.some((v) => v.id === current.id)) chooser.value = String(current.id);
      } else if (this.chooserEl) {
        this.chooserEl.hidden = true;
      }
      return this.applyGate(false);
    }

    const { pool, question, asked } = narrowParts(this.data, this.answers);
    if (question) {
      this.renderFollowups(asked, question);
      if (this.chooserEl) this.chooserEl.hidden = true;
      return this.applyGate(true);
    }
    this.renderFollowups(asked, null);

    // Nothing separates them: the SKU chooser.
    if (pool.length > 1) {
      this.renderChooser(pool);
      return this.applyGate(true);
    }

    if (this.chooserEl) this.chooserEl.hidden = true;
    const resolved = pool[0];
    if (resolved && resolved.id !== currentId) {
      this.autoSelectedId = resolved.id;
      selectVariant(this.scope, resolved.id);
    }
    return this.applyGate(false);
  }

  /**
   * Horizon always has a variant selected — the first one — so an untouched
   * Canopy page would add EA735146 to the cart for any truck. Hold the button
   * until the shopper has actually chosen. Spec §4.3.
   *
   * There is deliberately no "already in this state, skip it" memo. Eagle is
   * not the only writer of this DOM: on every product-select round trip Horizon
   * calls enable(), morphs the Add-to-cart button from fresh server HTML (wiping
   * disabled and aria-describedby), un-hides the accelerated-checkout container
   * and morphs the sticky bar. A memo desynchronises the moment that happens —
   * re-gating while a fetch is in flight left the gate permanently dead with
   * data-gate-active="true" still hiding the price. Every write below is
   * conditional or idempotent, so re-asserting costs nothing.
   *
   * @param {boolean} unresolved
   */
  applyGate(unresolved) {
    const wanted = unresolved && this.dataset.gate !== 'false' && (this.mode === 'vehicle' || this.mode === 'parts');
    this.dataset.gateActive = String(wanted);

    // Release gives back only what is sellable. Horizon says "sold out"
    // through the same switches the gate uses (blocks/buy-buttons.liquid sets
    // disabled whenever can_add_to_cart is false, and
    // blocks/accelerated-checkout.liquid sets hidden), and the gate marks a
    // control when it goes on whether or not Horizon had already switched it
    // off. So a page that loads on a sold-out variant, gated, then resolved to
    // that same variant (no fetch, so no fresh HTML from Horizon) would have
    // its release hand the sold-out variant a working Add to cart. The fit
    // block's payload says whether the selected variant is available: when it
    // is known to be unavailable, release drops the mark but leaves the control
    // off, which is Horizon's own state. An unknown variant is released as
    // before. Availability is read on every call, like everything else here.
    const selectedId = selectedVariantId(this.scope);
    const selected = (this.data && this.data.variants ? this.data.variants : []).find((v) => v.id === selectedId);
    const sellable = !selected || selected.available !== false;

    const helperId = `${this.dataset.blockId}-helper`;
    for (const button of this.scope.querySelectorAll('add-to-cart-component')) {
      if (typeof button.disable !== 'function') continue;
      // Give back only what the gate took, and only if it is sellable (above):
      // an unconditional enable() handed a working Add to cart to an
      // unavailable variant. The mark is ownership, not a memo: while the gate
      // is on it is re-asserted every render, which is what keeps it alive
      // across Horizon's re-renders.
      if (wanted) {
        button.dataset.eagleGated = 'true';
        button.disable();
      } else if (button.dataset.eagleGated) {
        delete button.dataset.eagleGated;
        if (sellable) button.enable();
      }

      const control = button.querySelector('[ref="addToCartButton"], button');
      if (control) {
        if (wanted) control.setAttribute('aria-describedby', helperId);
        // Ours to remove only while it still points at our own helper line.
        else if (control.getAttribute('aria-describedby') === helperId) control.removeAttribute('aria-describedby');
      }
    }

    // The sticky bar is a different element (sticky-add-to-cart), with a plain
    // button and no disable()/enable(). Its click is a puppet click on the main
    // form's button, so the cart is already safe — but left alone it still looks
    // and reads as enabled while doing nothing.
    for (const bar of this.scope.querySelectorAll('sticky-add-to-cart')) {
      const control = bar.querySelector('[ref="addToCartButton"], button');
      if (!control) continue;
      if (wanted) {
        control.dataset.eagleGated = 'true';
        control.disabled = true;
        control.setAttribute('aria-describedby', helperId);
      } else {
        if (control.dataset.eagleGated) {
          delete control.dataset.eagleGated;
          if (sellable) control.disabled = false;
        }
        if (control.getAttribute('aria-describedby') === helperId) control.removeAttribute('aria-describedby');
      }
    }

    // Accelerated checkout would bypass the gate entirely.
    for (const container of this.scope.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"]')) {
      // Horizon hides this for a sold-out variant too, so the same ownership
      // and sellable rules apply as for the button above.
      if (wanted) {
        container.dataset.eagleGated = 'true';
        container.hidden = true;
      } else if (container.dataset.eagleGated) {
        delete container.dataset.eagleGated;
        if (sellable) container.hidden = false;
      }
    }

    if (this.helperEl) this.helperEl.hidden = !wanted;
    this.renderGatedPrice(wanted);
  }

  /**
   * While gated the main price is hidden with visibility (space kept, so
   * nothing shifts) and this stands in for it.
   *
   * It stands in inside that kept space when the page has a <product-price>
   * block, so the buy box still reads title, price, divider instead of
   * leaving a blank band where the price was. The block's stylesheet
   * positions it absolutely there, so the box keeps exactly the height the
   * real price gave it and nothing moves when the gate goes on or off.
   * Horizon only swaps the [ref="priceContainer"] inside the element on a
   * variant change, so a sibling of it survives. With no price block on the
   * page it falls back to the fit box, so a gated product is never priceless.
   *
   * It stands in whether or not the prices vary. Returning early on a uniform
   * family left a gated multi-variant product with no price anywhere: the CSS
   * had already hidden the real one. "From $X" is only honest when there is a
   * range; when every variant costs the same, that one price is the price.
   *
   * @param {boolean} gated
   */
  renderGatedPrice(gated) {
    const details = this.closest('.product-details');
    const priceBlock = details ? details.querySelector('product-price') : null;
    const host = priceBlock || this;
    const found = [
      ...(priceBlock ? priceBlock.querySelectorAll('[data-role="from-price"]') : []),
      ...this.querySelectorAll('[data-role="from-price"]'),
    ];
    // Keep the one in the right place; anything elsewhere is stale.
    let el = null;
    for (const node of found) {
      if (gated && !el && host.contains(node)) el = node;
      else node.remove();
    }

    // The sticky bar's stand-in, read by the block's stylesheet as the bar
    // price's ::before. On the section, so Horizon's rebuilds of the bar can't
    // drop it; the stylesheet only shows it while the gate is on.
    const section = this.scope instanceof HTMLElement ? this.scope : null;
    const setSticky = (text) => {
      if (!section) return;
      const value = text ? JSON.stringify(text) : '';
      if (section.style.getPropertyValue('--eagle-from-price') === value) return;
      if (value) section.style.setProperty('--eagle-from-price', value);
      else section.style.removeProperty('--eagle-from-price');
    };

    if (!gated) return setSticky('');
    // leadPrice leaves out a missing price rather than coercing it to zero,
    // which would advertise "From $0.00".
    const lead = leadPrice(this.data && this.data.variants);
    if (!lead) return setSticky('');
    const text = lead.varies ? `From ${lead.text}` : lead.text;
    // The sticky bar's CSS content can't strike part of a string, so it
    // carries the price alone; the "was" price is in the buy box above it.
    setSticky(text);

    if (!el) {
      el = document.createElement('p');
      el.dataset.role = 'from-price';
      if (priceBlock) {
        el.className = 'eagle-gated-price';
        priceBlock.prepend(el);
      } else {
        el.className = 'eagle-fit-box__from';
        if (this.statusEl) this.statusEl.after(el);
        else this.prepend(el);
      }
    }
    // applyGate() now re-asserts on every render, so only write when it differs.
    const signature = JSON.stringify([text, lead.was]);
    if (el.dataset.signature !== signature) {
      el.dataset.signature = signature;
      el.textContent = text;
      appendWasPrice(el, lead.was, this.dataset.wasLabel || 'Regular price');
    }
  }

  explicitlyChosen() {
    return this.resolvedByPicker || this.dataset.variantExplicit === 'true';
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('eagle-vehicle-fit')) {
  customElements.define('eagle-vehicle-fit', EagleVehicleFit);
}

/* -------------------------------------------------------------- index -- */

/**
 * @typedef {Object} IndexItem
 * @property {number} id
 * @property {string} h - handle
 * @property {string} t - title
 * @property {string} p - price, pre-formatted by Shopify's money filter
 * @property {boolean} pv - price varies across variants
 * @property {?string} i - featured image URL (600px), or null
 * @property {boolean} u - universal: fits any truck; "v" is [] in this case
 * @property {?number} s - the variant id when the product is one purchasable variant, else null
 * @property {?string} cp - the compare-at ("was") price beside "p" when that variant is on sale, else null
 * @property {Array<string>} c - cat: collection handles
 * @property {Array<[string,string,number,number]>} v - [make, model, from, to] tuples
 */

// v3 added the card fields and universal products (see
// templates/collection.fitment.liquid). A v2 cache has neither, so it must not
// be read as v3. v4 added "s", the single-variant id the cart's one-click Add
// needs. v5 added "cp", the "was" price of a part on sale.
const INDEX_KEY = 'eagle-fitment-v5';
const INDEX_MAX_PAGES = 20;

/** The item format this module reads; templates/collection.fitment.liquid emits it as "version". */
export const INDEX_VERSION = 5;

/**
 * How long a cached index is reused. It carries prices for the recommendations
 * row, so this bounds how long an admin price change can take to show there.
 */
export const INDEX_TTL = 60 * 60 * 1000;

let indexPromise = null;

/** Forgets this page's fetched index, as a fresh page view would. */
export function __resetIndexForTests() {
  indexPromise = null;
}

/**
 * The cached index's items when they can be reused, otherwise null: the cache
 * must be of this format, younger than INDEX_TTL, and priced in the shopper's
 * current currency. When the page does not say its currency, that one check
 * is skipped rather than refetching on every view.
 *
 * @param {*} cached the parsed localStorage record
 * @param {{now?: number, currency?: ?string}} [context]
 * @returns {?Array<IndexItem>}
 */
export function readIndexCache(cached, { now = Date.now(), currency = activeCurrency() } = {}) {
  if (!cached || typeof cached !== 'object' || !Array.isArray(cached.items)) return null;
  if (cached.version !== INDEX_VERSION) return null;
  // Written as "not younger" so a non-numeric fetchedAt (NaN) is stale too.
  if (!(now - cached.fetchedAt < INDEX_TTL)) return null;
  if (currency && cached.currency !== currency) return null;
  return cached.items;
}

/** Drops earlier index caches so they are not left behind in every browser. */
function dropLegacyIndexCache() {
  for (const key of ['eagle-fitment-v1', 'eagle-fitment-v2', 'eagle-fitment-v3', 'eagle-fitment-v4']) {
    try {
      if (storage) storage.removeItem(key);
    } catch (error) {
      /* ignore */
    }
  }
}

/**
 * The catalogue's vehicle index, fetched once per page and cached for an hour
 * (see readIndexCache for what else a cache must match).
 * @param {string} endpoint
 * @returns {Promise<Array<IndexItem>>}
 */
export function loadIndex(endpoint = '/collections/all') {
  if (indexPromise) return indexPromise;
  dropLegacyIndexCache();

  indexPromise = (async () => {
    try {
      const reusable = readIndexCache(JSON.parse(storage ? storage.getItem(INDEX_KEY) : null));
      if (reusable) return reusable;
    } catch (error) {
      /* ignore a bad cache */
    }

    const fetchPage = async (page) => {
      const response = await fetch(`${endpoint}?view=fitment&page=${page}`);
      if (!response.ok) throw new Error(`fitment fetch failed: ${response.status}`);
      return response.json();
    };

    const first = await fetchPage(1);
    let items = first.items;
    const pages = Math.min(first.pages, INDEX_MAX_PAGES);
    if (first.pages > INDEX_MAX_PAGES) {
      console.warn(`[eagle-fitment] catalog spans ${first.pages} pages; indexing only the first ${INDEX_MAX_PAGES}.`);
    }
    for (let page = 2; page <= pages; page += 1) {
      items = items.concat((await fetchPage(page)).items);
    }

    // The page's template and this script come from different deploys: one of
    // them was cached. Use what arrived for this page view, but keep nothing
    // that a later view would take for the format it expects.
    if (first.version !== INDEX_VERSION) {
      console.warn(`[eagle-fitment] index format ${first.version} is not ${INDEX_VERSION}; not caching it.`);
      return items;
    }

    try {
      if (storage) {
        storage.setItem(
          INDEX_KEY,
          JSON.stringify({ version: INDEX_VERSION, currency: first.currency || null, fetchedAt: Date.now(), items })
        );
      }
    } catch (error) {
      console.warn('[eagle-fitment] could not cache the index; it will refetch each page view.', error);
    }
    return items;
  })();

  // A rejected promise is still truthy, so without this one transient failure
  // would be cached for the life of the page and no caller could ever retry.
  // The returned promise still rejects; this only clears the memo.
  indexPromise.catch(() => {
    indexPromise = null;
  });

  return indexPromise;
}

/**
 * Does this indexed product fit the vehicle? This is the year check the
 * vehicle: tags cannot do.
 * @param {{v: Array<[string,string,number,number]>}} item
 * @param {Vehicle} vehicle
 * @returns {boolean}
 */
export function indexItemFits(item, vehicle) {
  const key = canon(vehicle.make, vehicle.model).key;
  const year = Number(vehicle.year);
  return (item.v || []).some(([make, model, from, to]) =>
    canon(make, model).key === key && from <= year && year <= to
  );
}

/**
 * Does this indexed product share a vehicle with `entries`: the same make and
 * model, with overlapping years? The recommendations row's "shared fitment"
 * when no truck is saved, meaning parts for the trucks the viewed product fits.
 * @param {{v: Array<[string,string,number,number]>}} item
 * @param {Array<[string,string,number,number]>} entries  the viewed product's index entries
 * @returns {boolean}
 */
export function sharesFitment(item, entries) {
  const wanted = (entries || []).map(([make, model, from, to]) => [canon(make, model).key, from, to]);
  return (item.v || []).some(([make, model, from, to]) => {
    const key = canon(make, model).key;
    return wanted.some(([k, f, t]) => k === key && from <= t && f <= to);
  });
}

/**
 * Orders catalogue items for the product page's recommendations row.
 *
 * With a saved vehicle: (1) parts that fit it, from a category the current
 * product is not in — the cross-sell a canopy shopper actually needs, mud flaps
 * and floor liners for the same truck; (2) parts that fit it from the same
 * category — substitutes; (3) universal parts from other categories. Universal
 * parts in the same category are left to the section's server-rendered
 * baseline, which already shows those siblings. Within a tier, one product per
 * category in turn (round-robin), so one large family cannot fill the whole
 * row; categories are visited in the order of their first product's title, and
 * products within a category stay in title-then-id order, so the row does not
 * reshuffle between page views. Never the current product.
 *
 * Without a vehicle, "fits" means shares a fitment with the current product
 * (`fitment`, its index entries; see sharesFitment), in the same tiers. A
 * product with no vehicle entries (universal, or not in the index) has
 * nothing to share, so nothing is returned and the baseline stands.
 *
 * @param {IndexItem[]} items
 * @param {{vehicle: Vehicle|null, currentId: number|string, currentCats?: string[], fitment?: Array<[string,string,number,number]>, limit?: number}} options
 * @returns {IndexItem[]}
 */
export function rankRecommendations(items, { vehicle, currentId, currentCats = [], fitment = [], limit = 4 } = {}) {
  if (!Array.isArray(items)) return [];
  let fits = null;
  if (vehicle) fits = (item) => indexItemFits(item, vehicle);
  else if (fitment && fitment.length) fits = (item) => sharesFitment(item, fitment);
  if (!fits) return [];
  const own = new Set((currentCats || []).map(String));
  const sharesCategory = (item) => (item.c || []).some((handle) => own.has(String(handle)));
  const tiers = [[], [], []];
  for (const item of items) {
    if (!item || String(item.id) === String(currentId)) continue;
    if (fits(item)) tiers[sharesCategory(item) ? 1 : 0].push(item);
    else if (item.u && !sharesCategory(item)) tiers[2].push(item);
  }
  const byTitle = (a, b) =>
    String(a.t || '').localeCompare(String(b.t || ''), 'en') || String(a.id).localeCompare(String(b.id));
  // Within a tier, one product per category in turn, so one large family (the
  // side bars, whose titles lead with digits and sort first) cannot fill the
  // whole row. Deterministic: categories in the order of their first title,
  // products within a category by title then id.
  const interleave = (tier) => {
    const groups = new Map();
    for (const item of [...tier].sort(byTitle)) {
      const key = (item.c && item.c[0]) || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    }
    const queues = [...groups.values()];
    const out = [];
    for (let round = 0; queues.some((q) => q.length > round); round += 1) {
      for (const q of queues) if (q.length > round) out.push(q[round]);
    }
    return out;
  };
  return tiers.flatMap(interleave).slice(0, Math.max(0, limit));
}

/**
 * Which of the section's server-rendered baseline cards may join a fitted row.
 *
 * Only cards the index says fit the vehicle, or that are universal: the row's
 * heading tells the shopper every card fits their truck, and a baseline sibling
 * is otherwise unchecked — it may be listed for other vehicles only, or be
 * absent from the index altogether. Keeps baseline order; skips ids already
 * taken; stops at `room`.
 *
 * @param {Array<number|string>} baselineIds  product ids of the baseline cards, in order
 * @param {IndexItem[]} items
 * @param {Vehicle} vehicle
 * @param {Set<string>} taken  ids already in the row (strings)
 * @param {number} room        how many more cards the row can take
 * @returns {string[]}
 */
export function verifiedTopUp(baselineIds, items, vehicle, taken, room) {
  if (!vehicle || room <= 0) return [];
  const byId = new Map((items || []).map((item) => [String(item.id), item]));
  const out = [];
  for (const raw of baselineIds || []) {
    if (out.length >= room) break;
    const id = String(raw);
    if (taken.has(id)) continue;
    const item = byId.get(id);
    if (item && (item.u || indexItemFits(item, vehicle))) out.push(id);
  }
  return out;
}

/**
 * Puts the product's Search & Discovery picks (complementary, then related) at
 * the front of the row.
 *
 * Picks keep the order staff gave them; the current product and repeats are
 * dropped. With a saved vehicle, a pick joins only if the index says it fits
 * or is universal — the row's heading promises every card fits the truck, and
 * a pick missing from the index is unchecked, so it is left out. The row's
 * other cards follow, minus any already placed, up to `limit`.
 *
 * @param {Array<number|string>} recommendedIds  Search & Discovery picks, complementary then related
 * @param {Array<number|string>} restIds  the row as it would otherwise be, in order
 * @param {{limit?: number, currentId?: number|string, vehicle?: Vehicle|null, items?: IndexItem[]}} options
 * @returns {string[]} product ids (strings), in display order
 */
export function recommendedFirst(recommendedIds, restIds, { limit = 4, currentId, vehicle = null, items = [] } = {}) {
  const byId = vehicle ? new Map((items || []).map((item) => [String(item.id), item])) : null;
  const verified = (id) => {
    if (!byId) return true;
    const item = byId.get(id);
    return Boolean(item && (item.u || indexItemFits(item, vehicle)));
  };
  const out = [];
  const taken = new Set();
  if (currentId != null) taken.add(String(currentId));
  const place = (raw, check) => {
    const id = String(raw);
    if (out.length >= limit || taken.has(id) || (check && !verified(id))) return;
    taken.add(id);
    out.push(id);
  };
  for (const id of recommendedIds || []) place(id, true);
  for (const id of restIds || []) place(id, false);
  return out;
}

/**
 * The product page's recommendations row, as ids in display order.
 *
 * A product with complementary picks shows only those: no related picks and
 * no fitment ranking, because staff chose exactly what goes with it. With a
 * saved truck, only the picks that fit it or are universal count (see
 * recommendedFirst). When none count, or there are none, the row is as it was
 * before: the related picks, then `restIds`.
 *
 * @param {Array<number|string>} complementaryIds  Search & Discovery complementary picks, in staff order
 * @param {Array<number|string>} relatedIds  Search & Discovery related picks
 * @param {Array<number|string>} restIds  the row as it would otherwise be, in order
 * @param {{limit?: number, currentId?: number|string, vehicle?: Vehicle|null, items?: IndexItem[]}} options
 * @returns {string[]}
 */
export function recommendationRow(complementaryIds, relatedIds, restIds, options = {}) {
  const complementary = recommendedFirst(complementaryIds, [], options);
  return complementary.length ? complementary : recommendedFirst(relatedIds, restIds, options);
}

/* ---------------------------------------------- DOM: eagle-fitment-bar -- */

class EagleFitmentBar extends HTMLElementBase {
  connectedCallback() {
    this.endpoint = this.dataset.endpoint || '/collections/all';
    this.pageKind = this.dataset.pageKind || 'other';
    this.collectionUrl = this.dataset.collectionUrl || '';

    this.trigger = this.querySelector('[data-role="trigger"]');
    this.triggerLabel = this.querySelector('[data-role="trigger-label"]');
    this.defaultTriggerLabel = this.triggerLabel ? this.triggerLabel.textContent.trim() : 'Find your fit';
    this.panel = this.querySelector('[data-role="panel"]');
    this.searchForm = this.querySelector('[data-role="search"]');
    this.clearButton = this.querySelector('[data-role="clear"]');
    this.submitButton = this.querySelector('[data-role="submit"]');
    this.shopAllLink = this.querySelector('[data-role="shop-all"]');
    this.announcerEl = this.querySelector('[data-role="announcer"]');
    this.selects = {
      year: this.querySelector('[data-role="year"]'),
      make: this.querySelector('[data-role="make"]'),
      model: this.querySelector('[data-role="model"]'),
    };
    this.placeholders = {
      year: this.selects.year.options[0].textContent,
      make: this.selects.make.options[0].textContent,
      model: this.selects.model.options[0].textContent,
    };
    this.hints = { year: 'still loading', make: 'choose a year first', model: 'choose a make first' };
    for (const kind of ['year', 'make', 'model']) this.setAvailability(kind, false);

    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('pointerenter', () => this.startLoading(false), { once: true });
    this.trigger.addEventListener('focus', () => this.startLoading(false), { once: true });
    this.querySelector('[data-role="close"]').addEventListener('click', () => this.close(true));
    this.panel.querySelector('form').addEventListener('submit', (event) => this.onSubmit(event));
    this.selects.year.addEventListener('change', () => this.onYearChange());
    this.selects.make.addEventListener('change', () => this.onMakeChange());
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        clearVehicle();
        for (const kind of ['year', 'make', 'model']) this.selects[kind].value = '';
        this.onYearChange();
        this.announce('Vehicle cleared.');
      });
    }

    this.onDocumentKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.close(true);
    };
    this.onDocumentClick = (event) => {
      if (!this.isOpen()) return;
      if (this.panel.contains(event.target) || this.trigger.contains(event.target)) return;
      this.close(false);
    };
    this.onVehicleChange = () => {
      this.reflectVehicle();
      this.renderSuggest();
    };
    document.addEventListener('keydown', this.onDocumentKeydown);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('eagle:vehicle-change', this.onVehicleChange);
    window.addEventListener('storage', this.onVehicleChange);
    window.addEventListener('pageshow', this.onVehicleChange);

    this.placeInHeader();
    this.reflectVehicle();

    // A collection page with a saved vehicle needs the index for its own
    // refinement layer, so warm it now rather than on intent.
    if (this.pageKind === 'collection' && getVehicle()) this.startLoading(false);

    this.wireSuggest();

    // The search page found nothing: the Liquid hands over the terms.
    if (this.dataset.searchRetry) this.retryPartSearch(this.dataset.searchRetry);
  }

  /**
   * A part number typed without its "EA" finds nothing (partNumberRetry).
   * Predictive search is asked first whether the prefixed number finds a
   * product, and only then is the page reloaded with it, so a real miss keeps
   * the shopper's own words in the "No results" line. replace() keeps Back
   * from landing on the empty page again.
   */
  async retryPartSearch(term) {
    const retry = partNumberRetry(term);
    const base = this.dataset.suggestUrl;
    if (!retry || !base || typeof fetch !== 'function') return;
    try {
      if (!(await suggestProducts(base, retry, 1)).length) return;
      const url = new URL(location.href);
      url.searchParams.set('q', retry);
      location.replace(url.toString());
    } catch (error) {
      /* the "No results" page stands */
    }
  }

  /* ------------------------------------------------------ search list -- */

  /**
   * The header search lists matching products as the shopper types, as the TTO
   * site does (Eagle, 2026-10-29). An ARIA 1.2 combobox: focus stays in the
   * field, the arrow keys move aria-activedescendant through a listbox, Enter
   * opens the active row or, with none, searches as before, and Escape closes
   * the list. Rows are options rather than links, so the listbox holds nothing
   * focusable; a click opens a row and a Ctrl/Cmd-click opens it in a new tab.
   */
  wireSuggest() {
    const form = this.searchForm;
    if (!form || !this.dataset.suggestUrl || typeof fetch !== 'function') return;
    this.suggestInput = form.querySelector('input[name="q"]');
    this.suggestEl = form.querySelector('[data-role="suggest"]');
    this.suggestList = form.querySelector('[role="listbox"]');
    this.suggestEmpty = form.querySelector('[data-role="suggest-empty"]');
    this.suggestStatus = form.querySelector('[data-role="suggest-status"]');
    if (!this.suggestInput || !this.suggestEl || !this.suggestList) return;

    const input = this.suggestInput;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', this.suggestList.id);
    input.setAttribute('aria-expanded', 'false');
    this.suggestActive = -1;
    this.suggestTerm = '';

    let timer = null;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      this.setSuggestActive(-1);
      const term = input.value.trim();
      if (term.length < 2) {
        if (this.suggestAbort) this.suggestAbort.abort();
        this.suggestTerm = '';
        this.suggestProducts = null;
        this.closeSuggest();
        return;
      }
      timer = setTimeout(() => this.fetchSuggest(term), 200);
    });
    input.addEventListener('keydown', (event) => this.onSuggestKey(event));
    input.addEventListener('focus', () => {
      if (this.suggestProducts && this.suggestTerm === input.value.trim()) this.openSuggest();
    });
    form.addEventListener('focusout', (event) => {
      if (!form.contains(event.relatedTarget)) this.closeSuggest();
    });
    // Keeps focus in the field while a row is clicked.
    this.suggestEl.addEventListener('mousedown', (event) => event.preventDefault());
    this.suggestList.addEventListener('click', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.chooseSuggest(Number(option.dataset.index), event.ctrlKey || event.metaKey);
    });
    // A Back navigation restores the page from the bfcache with the list open.
    this.onSuggestPageShow = () => this.closeSuggest();
    window.addEventListener('pageshow', this.onSuggestPageShow);
  }

  /**
   * Asks predictive search for the term, and again with "EA" in front when a
   * part number found nothing (partNumberRetry). A newer keystroke aborts the
   * older request. With a truck saved, the catalogue index is fetched too,
   * for the fit labels; the rows show first and gain their labels when it lands.
   */
  async fetchSuggest(term) {
    if (this.suggestAbort) this.suggestAbort.abort();
    const abort = new AbortController();
    this.suggestAbort = abort;
    const base = this.dataset.suggestUrl;
    try {
      let query = term;
      let products = await suggestProducts(base, term, SUGGEST_LIMIT, abort.signal);
      const retry = products.length ? null : partNumberRetry(term);
      if (retry) {
        const retried = await suggestProducts(base, retry, SUGGEST_LIMIT, abort.signal);
        if (retried.length) {
          products = retried;
          query = retry;
        }
      }
      if (abort.signal.aborted) return;
      this.suggestTerm = term;
      this.suggestQuery = query;
      this.suggestProducts = products;
      if (getVehicle() && !this.items) this.startLoading(false);
      this.renderSuggest();
      this.openSuggest();
      if (this.suggestStatus) {
        this.suggestStatus.textContent = products.length
          ? `${products.length} ${products.length === 1 ? 'product' : 'products'} found`
          : 'No products found';
      }
    } catch (error) {
      /* Aborted by newer typing, or search is down: Enter still searches. */
    }
  }

  /** Rebuilds the rows from the last answer, keeping the active row. */
  renderSuggest() {
    if (!this.suggestList || !this.suggestProducts) return;
    const rows = suggestRows(this.suggestProducts, {
      query: this.suggestQuery,
      vehicle: getVehicle(),
      items: this.items || null,
    });
    this.suggestData = rows;

    const listId = this.suggestList.id;
    const option = (index, className) => {
      const li = document.createElement('li');
      li.className = className;
      li.id = `${listId}-${index}`;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.dataset.index = String(index);
      return li;
    };
    const span = (className, text) => {
      const el = document.createElement('span');
      el.className = className;
      if (text != null) el.textContent = text;
      return el;
    };

    const nodes = rows.map((row, index) => {
      const li = option(index, 'eagle-search__option');
      const media = span('eagle-search__media');
      if (row.image) {
        const img = document.createElement('img');
        img.src = row.image;
        img.alt = '';
        img.width = 48;
        img.height = 36;
        media.append(img);
      }
      const text = span('eagle-search__text');
      if (row.fitText) {
        const fit = span(`eagle-search__fit eagle-search__fit--${row.fit}`);
        const glyph = span('eagle-search__fit-icon', row.fit === 'fits' ? '✓' : '✕');
        glyph.setAttribute('aria-hidden', 'true');
        fit.append(glyph, ` ${row.fitText}`);
        text.append(fit);
      }
      text.append(span('eagle-search__title', row.title));
      const about = [row.part && `Part ${row.part}`, row.detail].filter(Boolean).join(' · ');
      if (about) text.append(span('eagle-search__about', about));
      const price = span('eagle-search__price', row.price);
      appendWasPrice(price, row.was, this.dataset.wasLabel || 'Regular price');
      text.append(price);
      li.append(media, text);
      return li;
    });
    if (rows.length) {
      const all = option(rows.length, 'eagle-search__option eagle-search__option--all');
      all.textContent = `View all results for “${this.suggestTerm}”`;
      nodes.push(all);
    }

    const keep = this.suggestActive;
    this.suggestList.replaceChildren(...nodes);
    this.suggestList.hidden = !rows.length;
    if (this.suggestEmpty) {
      this.suggestEmpty.hidden = rows.length > 0;
      this.suggestEmpty.textContent = rows.length ? '' : `No products found for “${this.suggestTerm}”`;
    }
    this.setSuggestActive(keep);
  }

  isSuggestOpen() {
    return !!this.suggestEl && !this.suggestEl.hidden;
  }

  openSuggest() {
    if (!this.suggestEl || !this.suggestProducts) return;
    this.suggestEl.hidden = false;
    this.suggestInput.setAttribute('aria-expanded', 'true');
  }

  closeSuggest() {
    if (!this.suggestEl) return;
    this.suggestEl.hidden = true;
    this.suggestInput.setAttribute('aria-expanded', 'false');
    this.setSuggestActive(-1);
  }

  setSuggestActive(index) {
    if (!this.suggestList) return;
    const options = [...this.suggestList.querySelectorAll('[role="option"]')];
    this.suggestActive = index >= 0 && index < options.length ? index : -1;
    options.forEach((el, i) => {
      const on = i === this.suggestActive;
      el.setAttribute('aria-selected', on ? 'true' : 'false');
      el.classList.toggle('is-active', on);
    });
    if (this.suggestActive >= 0) {
      this.suggestInput.setAttribute('aria-activedescendant', options[this.suggestActive].id);
      options[this.suggestActive].scrollIntoView({ block: 'nearest' });
    } else {
      this.suggestInput.removeAttribute('aria-activedescendant');
    }
  }

  onSuggestKey(event) {
    const count = this.suggestList.querySelectorAll('[role="option"]').length;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!count || !this.suggestProducts) return;
      event.preventDefault();
      this.openSuggest();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const from = this.suggestActive < 0 && step < 0 ? count : this.suggestActive;
      this.setSuggestActive((from + step + count) % count);
    } else if (event.key === 'Enter') {
      if (this.isSuggestOpen() && this.suggestActive >= 0) {
        event.preventDefault();
        this.chooseSuggest(this.suggestActive, event.ctrlKey || event.metaKey);
      }
    } else if (event.key === 'Escape' && this.isSuggestOpen()) {
      // Stops the document's Escape handler and the field's own clearing.
      event.preventDefault();
      event.stopPropagation();
      this.closeSuggest();
    }
  }

  /** Opens a product row, or for the "View all" row runs the search as Enter would. */
  chooseSuggest(index, newTab) {
    const row = (this.suggestData || [])[index];
    if (!row) {
      this.closeSuggest();
      this.searchForm.requestSubmit();
    } else if (newTab) {
      window.open(row.url, '_blank', 'noopener');
    } else {
      location.assign(row.url);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('eagle:vehicle-change', this.onVehicleChange);
    window.removeEventListener('storage', this.onVehicleChange);
    window.removeEventListener('pageshow', this.onVehicleChange);
    window.removeEventListener('pageshow', this.onSuggestPageShow);
    this.stopTrackingPanel();
  }

  /* Unchanged from the previous inline class: Horizon has no header slots, so
     the trigger moves into the navigation row and the search into the top
     row's centre column, on desktop only. */
  placeInHeader() {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    const header = document.querySelector('header-component');
    if (!header) return;
    const rows = header.querySelectorAll('.header__columns');
    const top = rows[0];
    const nav = rows[1];
    if (nav) nav.append(this.trigger);
    if (top && this.searchForm) {
      let centre = top.querySelector('.header__column--center');
      if (!centre) {
        centre = document.createElement('div');
        centre.className = 'header__column header__column--center';
        const right = top.querySelector('.header__column--right');
        if (right) top.insertBefore(centre, right);
        else top.append(centre);
      }
      centre.append(this.searchForm);
    }
  }

  /** The trigger reads the saved vehicle; only its own width changes. */
  reflectVehicle() {
    const vehicle = getVehicle();
    if (this.triggerLabel) {
      this.triggerLabel.textContent = vehicle
        ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`.toUpperCase()
        : this.defaultTriggerLabel;
    }
    this.trigger.setAttribute(
      'aria-label',
      vehicle
        ? `Your vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}. Change vehicle`
        : this.defaultTriggerLabel
    );
    if (this.clearButton) this.clearButton.hidden = !vehicle;
    if (this.shopAllLink && vehicle) {
      this.shopAllLink.href = `/collections/all/${vehicleTagHandle(vehicle)}`;
      this.shopAllLink.hidden = this.pageKind !== 'product';
    } else if (this.shopAllLink) {
      this.shopAllLink.hidden = true;
    }
  }

  isOpen() { return !this.panel.hidden; }
  toggle() { if (this.isOpen()) this.close(true); else this.open(); }

  /** Public: the product page's "Change vehicle" calls this. */
  open() {
    this.panel.hidden = false;
    this.positionPanel();
    this.trigger.setAttribute('aria-expanded', 'true');
    this.startLoading(true);
    this.selects.year.focus();
  }

  close(returnFocus) {
    this.panel.hidden = true;
    this.stopTrackingPanel();
    this.trigger.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.trigger.focus();
  }

  /**
   * Hangs the panel off the bottom edge of the sticky header that
   * placeInHeader() moved the trigger into.
   *
   * The stylesheet pins it to --header-height, which is the header
   * component's own height and knows nothing about the announcement and
   * utility bars stacked above it. At the top of the page the header ends at
   * 207px while that variable reads 60px, so the panel opened 147px too high,
   * behind the header, and all the shopper saw was the sliver of it below the
   * nav row. It looked right once scrolled, because those bars scroll away and
   * the two numbers converge — which is exactly why it went unnoticed.
   *
   * Measured when the panel opens and tracked while it is open, so it follows
   * the header whatever the header is doing. The trigger lives in the header
   * and the panel does not, so the header is found from the trigger; falling
   * back to this element, which sits directly below the header anyway.
   */
  positionPanel() {
    if (!this.panel || this.panel.hidden) return;
    // On phones the panel opens in flow, where an inline top would do nothing.
    if (typeof getComputedStyle === 'function' && getComputedStyle(this.panel).position !== 'fixed') {
      this.panel.style.removeProperty('top');
      this.stopTrackingPanel();
      return;
    }
    const header = (this.trigger && this.trigger.closest('header-component, .shopify-section-group-header-group')) || this;
    const bottom = header.getBoundingClientRect().bottom;
    if (bottom > 0) this.panel.style.top = `${Math.round(bottom)}px`;

    if (!this.trackPanel) {
      this.trackPanel = () => this.positionPanel();
      // Capture, because Horizon scrolls the page inside .page-wrapper rather
      // than the document, and a scroll event on an element does not bubble.
      document.addEventListener('scroll', this.trackPanel, { passive: true, capture: true });
      window.addEventListener('resize', this.trackPanel);
    }
  }

  stopTrackingPanel() {
    if (!this.trackPanel) return;
    document.removeEventListener('scroll', this.trackPanel, { capture: true });
    window.removeEventListener('resize', this.trackPanel);
    this.trackPanel = null;
  }

  startLoading(userInitiated) {
    // Already loaded: nothing to fetch, just announce if the shopper asked.
    if (this.items) {
      if (userInitiated && !this.readyAnnounced) this.announceReady();
      return;
    }
    // A fetch is already in flight — let it finish.
    if (this.loadInFlight) return;
    // A previous attempt failed. Retry only when the shopper actually asked,
    // so a failed hover prefetch does not refetch on every pointer movement.
    if (this.loadStarted && !userInitiated) return;

    this.loadStarted = true;
    this.loadInFlight = true;
    loadIndex(this.endpoint)
      .then((items) => {
        this.loadInFlight = false;
        this.items = items;
        this.fillSelect('year', this.years(), true);
        this.prefill();
        // The search list's fit labels wait for the index.
        this.renderSuggest();
        if (userInitiated || this.isOpen()) this.announceReady();
      })
      .catch(() => {
        this.loadInFlight = false;
        if (userInitiated || this.isOpen()) {
          this.announce('Vehicle search is unavailable right now. Use Shop to browse all products.');
        }
      });
  }

  announceReady() {
    this.readyAnnounced = true;
    this.announce(`Vehicle search ready. ${this.selects.year.options.length - 1} years available.`);
  }

  /** Every year any tuple covers, newest first. */
  years() {
    const values = new Set();
    for (const item of this.items || []) {
      for (const [, , from, to] of item.v) for (let y = from; y <= to; y += 1) values.add(y);
    }
    return new Map([...values].sort((a, b) => b - a).map((y) => [String(y), String(y)]));
  }

  /** Makes with a tuple covering the chosen year; aliases applied on read. */
  makes(year) {
    const values = new Map();
    for (const item of this.items || []) {
      for (const [make, model, from, to] of item.v) {
        if (from <= year && year <= to) {
          const c = canon(make, model);
          values.set(c.make.toLowerCase(), c.make);
        }
      }
    }
    return values;
  }

  models(year, makeKey) {
    const values = new Map();
    for (const item of this.items || []) {
      for (const [make, model, from, to] of item.v) {
        if (!(from <= year && year <= to)) continue;
        const c = canon(make, model);
        if (c.make.toLowerCase() !== makeKey) continue;
        values.set(c.model.toLowerCase(), c.model);
      }
    }
    return values;
  }

  fillSelect(kind, values, numericDesc = false) {
    const select = this.selects[kind];
    select.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.placeholders[kind];
    select.append(placeholder);
    const keys = [...values.keys()].sort(
      numericDesc
        ? (a, b) => (Number(b) || 0) - (Number(a) || 0)
        : (a, b) => a.localeCompare(b, undefined, { numeric: true })
    );
    for (const key of keys) {
      const option = document.createElement('option');
      option.value = key;
      option.textContent = values.get(key);
      select.append(option);
    }
    this.setAvailability(kind, keys.length > 0);
    return keys.length;
  }

  resetSelect(kind) {
    this.fillSelect(kind, new Map());
  }

  setAvailability(kind, available) {
    const select = this.selects[kind];
    select.setAttribute('aria-disabled', available ? 'false' : 'true');
    select.setAttribute(
      'aria-label',
      available ? this.placeholders[kind] : `${this.placeholders[kind]}, ${this.hints[kind]}`
    );
  }

  announce(message) {
    if (this.announcerEl) this.announcerEl.textContent = message || '';
  }

  /** The panel opens pre-filled when a vehicle is saved. */
  prefill() {
    const vehicle = getVehicle();
    if (!vehicle || !this.items) return;
    this.selects.year.value = String(vehicle.year);
    if (!this.selects.year.value) return;
    this.fillSelect('make', this.makes(vehicle.year));
    this.selects.make.value = vehicle.make.toLowerCase();
    if (!this.selects.make.value) return;
    this.fillSelect('model', this.models(vehicle.year, vehicle.make.toLowerCase()));
    this.selects.model.value = vehicle.model.toLowerCase();
  }

  onYearChange() {
    const year = Number(this.selects.year.value);
    this.resetSelect('model');
    if (!year) {
      this.resetSelect('make');
      this.announce('Year cleared. Choose a year to list makes.');
      return;
    }
    const count = this.fillSelect('make', this.makes(year));
    this.announce(
      count
        ? `${count} ${count === 1 ? 'make' : 'makes'} available for ${year}. Choose a make next.`
        : `No makes listed for ${year}.`
    );
  }

  onMakeChange() {
    const year = Number(this.selects.year.value);
    const makeKey = this.selects.make.value;
    if (!year || !makeKey) {
      this.resetSelect('model');
      this.announce('Make cleared. Choose a make to list models.');
      return;
    }
    const count = this.fillSelect('model', this.models(year, makeKey));
    const label = this.selects.make.selectedOptions[0].textContent;
    this.announce(
      count
        ? `${count} ${count === 1 ? 'model' : 'models'} available for ${label}. Choose a model, or search now.`
        : `No models listed for ${label}.`
    );
  }

  onSubmit(event) {
    event.preventDefault();
    const year = Number(this.selects.year.value);
    const make = this.selects.make.selectedOptions[0] ? this.selects.make.selectedOptions[0].textContent : '';
    const model = this.selects.model.selectedOptions[0] ? this.selects.model.selectedOptions[0].textContent : '';
    if (!year || !this.selects.make.value || !this.selects.model.value) {
      this.announce('Choose a year, make and model first.');
      return;
    }

    const vehicle = setVehicle({ year, make, model });
    if (!vehicle) return;
    const handle = vehicleTagHandle(vehicle);

    // On a product page the page reacts through eagle:vehicle-change — going
    // somewhere else would throw away the product the shopper is looking at.
    if (this.pageKind === 'product') {
      this.close(true);
      return;
    }
    const base = this.pageKind === 'collection' && this.collectionUrl ? this.collectionUrl : this.endpoint;
    window.location.assign(`${base}/${handle}`);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('eagle-fitment-bar')) {
  customElements.define('eagle-fitment-bar', EagleFitmentBar);
}

/* ------------------------------------------- DOM: eagle-collection-fit -- */

const ONLY_FITS_KEY = 'eagle-only-fits';

/**
 * Whether "Only show what fits my truck" is on — that is, whether products
 * that do not fit the saved vehicle are hidden rather than dimmed.
 *
 * - No saved vehicle: off. There is nothing to filter against.
 * - A vehicle tag URL: on. The tag already filters, so the toggle is hidden
 *   there and the stored choice does not apply to it.
 * - A plain collection URL: on, unless the shopper turned it off this session.
 *
 * @param {{hasVehicle: boolean, onTagUrl: boolean, stored: ('on'|'off'|null)}} state
 *   `stored` is the choice remembered for this browsing session, or null when
 *   the shopper has not touched the toggle.
 * @returns {boolean}
 */
export function onlyFitsDefault({ hasVehicle, onTagUrl, stored }) {
  if (!hasVehicle) return false;
  if (onTagUrl) return true;
  return stored !== 'off';
}

/**
 * The collection bar's status line. Hidden products are counted as ones that
 * do not fit, not as "other years": most fail on make or model.
 *
 * @param {Vehicle} vehicle
 * @param {{hidden: number, fits: number}} counts
 * @returns {string}
 */
export function fitSummary(vehicle, { hidden, fits }) {
  const parts = [`Showing parts for your ${vehicle.year} ${vehicle.make} ${vehicle.model}`];
  if (hidden) parts.push(`${hidden} hidden that ${hidden === 1 ? "doesn't" : "don't"} fit`);
  else if (fits) parts.push(`${fits} ${fits === 1 ? 'fits' : 'fit'}`);
  return parts.join(' · ');
}

/**
 * Whether a vehicle tag (e.g. "vehicle:GMC Sierra 1500") is the saved truck's
 * make and model. Compared as handles, the way Shopify builds the tag URL, and
 * through the alias table, since the store tags the same truck both ways.
 *
 * @param {string} tag
 * @param {Vehicle} vehicle
 * @returns {boolean}
 */
export function tagIsForVehicle(tag, vehicle) {
  if (!tag || !vehicle) return false;
  const wanted = canon(vehicle.make, vehicle.model).key;
  const handles = new Set([vehicleTagHandle(vehicle), handleize(`vehicle:${vehicle.make} ${vehicle.model}`)]);
  for (const alias of VEHICLE_ALIASES) {
    if (canon(...alias.from).key === wanted) handles.add(handleize(`vehicle:${alias.from.join(' ')}`));
  }
  return handles.has(handleize(tag));
}

/**
 * The status line on a tag URL for a truck other than the saved one: what the
 * saved truck is, and a link to its own tag URL in this collection.
 *
 * @param {Vehicle} vehicle
 * @param {string} collectionUrl
 * @returns {{text: string, linkText: string, href: string}}
 */
export function tagMismatch(vehicle, collectionUrl) {
  return {
    text: `Your saved truck is a ${vehicle.year} ${vehicle.make} ${vehicle.model}.`,
    linkText: 'See parts for it',
    href: `${collectionUrl}/${vehicleTagHandle(vehicle)}`,
  };
}

/** This session's remembered toggle choice, 'on' or 'off', or null. Blocked storage reads as null. */
function readOnlyFitsChoice() {
  try {
    const value = sessionStorage.getItem(ONLY_FITS_KEY);
    return value === 'on' || value === 'off' ? value : null;
  } catch (error) {
    return null;
  }
}

class EagleCollectionFit extends HTMLElementBase {
  connectedCallback() {
    this.collectionUrl = this.dataset.collectionUrl || '';
    this.onTagUrl = (this.dataset.vehicleTag || '') !== '';
    this.summaryEl = this.querySelector('[data-role="summary"]');
    this.actionsEl = this.querySelector('[data-role="actions"]');
    this.toggleWrap = this.querySelector('[data-role="toggle-wrap"]');
    this.toggle = this.querySelector('[data-role="toggle"]');

    this.changeEl = this.querySelector('[data-role="change"]');
    this.clearEl = this.querySelector('[data-role="clear"]');

    this.onChangeClick = (event) => {
      // See wireActions() above: the bar's click-away handler is on document,
      // so an un-stopped click would close the panel this very click opened.
      event.stopPropagation();
      const bar = document.querySelector('eagle-fitment-bar');
      if (bar && typeof bar.open === 'function') bar.open();
    };
    this.onClearClick = () => this.clearAndRefocus();

    // The shopper's toggle choice, held here as well as in sessionStorage so
    // it survives a page view where storage is blocked: apply() re-derives the
    // checkbox from it on every grid mutation. null until they touch it.
    this.onlyFitsChoice = readOnlyFitsChoice();
    this.onToggleChange = () => {
      this.onlyFitsChoice = this.toggle.checked ? 'on' : 'off';
      try {
        sessionStorage.setItem(ONLY_FITS_KEY, this.onlyFitsChoice);
      } catch (error) {
        /* ignore: the in-memory choice above still holds for this page view */
      }
      this.apply();
    };

    if (this.changeEl) this.changeEl.addEventListener('click', this.onChangeClick);
    if (this.clearEl) this.clearEl.addEventListener('click', this.onClearClick);
    if (this.toggle) this.toggle.addEventListener('change', this.onToggleChange);

    this.onVehicleChange = () => this.refresh();
    document.addEventListener('eagle:vehicle-change', this.onVehicleChange);
    window.addEventListener('storage', this.onVehicleChange);
    window.addEventListener('pageshow', this.onVehicleChange);

    // Horizon re-renders the grid for facets, sorting and infinite scroll.
    // apply() only ever mutates a card's childList (the badge) when the
    // card's state actually needs to change — see setCard()/clearCard() —
    // so a re-application that changes nothing produces no childList
    // mutation and cannot retrigger this observer forever.
    this.results = document.querySelector('#ResultsList');
    if (this.results) {
      this.observer = new MutationObserver(() => this.apply());
      this.observer.observe(this.results, { childList: true, subtree: true });
    }

    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('eagle:vehicle-change', this.onVehicleChange);
    window.removeEventListener('storage', this.onVehicleChange);
    window.removeEventListener('pageshow', this.onVehicleChange);
    if (this.changeEl) this.changeEl.removeEventListener('click', this.onChangeClick);
    if (this.clearEl) this.clearEl.removeEventListener('click', this.onClearClick);
    if (this.toggle) this.toggle.removeEventListener('change', this.onToggleChange);
    if (this.observer) this.observer.disconnect();
  }

  /**
   * Clear hides the row it lives in (apply() hides [data-role="actions"] once
   * there is no vehicle), so a keyboard user's focus would fall to <body>.
   * Hand it to the header's "Find your fit" trigger — the natural next step —
   * or, failing that, to this element.
   */
  clearAndRefocus() {
    const hadFocus = this.clearEl && this.clearEl.contains(document.activeElement);
    clearVehicle();
    if (!hadFocus) return;
    const trigger = document.querySelector('.eagle-fit__trigger');
    if (trigger && trigger.getClientRects().length && !trigger.closest('[hidden]')) {
      trigger.focus();
      return;
    }
    this.setAttribute('tabindex', '-1');
    this.focus();
  }

  refresh() {
    // pageshow restores a page from the back/forward cache holding the choice
    // it had when the shopper left, and they may have changed it since.
    const stored = readOnlyFitsChoice();
    if (stored) this.onlyFitsChoice = stored;

    const vehicle = getVehicle();
    if (!vehicle) {
      this.byId = null;
      this.apply();
      return;
    }
    loadIndex()
      .then((items) => {
        this.byId = new Map(items.map((item) => [String(item.id), item]));
        this.apply();
      })
      .catch(() => {
        this.byId = null;
        this.apply();
      });
  }

  apply() {
    const vehicle = getVehicle();
    const cards = this.results ? this.results.querySelectorAll('li.product-grid__item[data-product-id]') : [];

    // A tag URL for another truck: a shared link, or a page opened before the
    // saved vehicle changed. Refining it by the saved truck hid every product
    // on it. Leave the grid as the tag filtered it and point to the saved
    // truck's own page instead. This needs no index.
    if (vehicle && this.onTagUrl && !tagIsForVehicle(this.dataset.vehicleTag, vehicle)) {
      for (const card of cards) this.clearCard(card);
      this.showOnlyFits(false);
      this.showMismatch(tagMismatch(vehicle, this.collectionUrl));
      if (this.actionsEl) this.actionsEl.hidden = false;
      if (this.toggleWrap) this.toggleWrap.hidden = true;
      return;
    }

    // No vehicle, or no index: leave every card exactly as Shopify rendered it.
    if (!vehicle || !this.byId) {
      for (const card of cards) this.clearCard(card);
      this.showOnlyFits(false);
      if (this.summaryEl) this.summaryEl.hidden = true;
      if (this.actionsEl) this.actionsEl.hidden = true;
      return;
    }

    const onlyFits = onlyFitsDefault({
      hasVehicle: true,
      onTagUrl: this.onTagUrl,
      stored: this.onlyFitsChoice,
    });
    this.showOnlyFits(onlyFits);
    let hidden = 0;
    let fits = 0;

    for (const card of cards) {
      const item = this.byId.get(String(card.dataset.productId));

      // Not in the index, or universal — which the index now carries for the
      // product-page recommendations. Either way this bar has nothing to say:
      // a universal part fits every truck, and an empty vehicle list must not
      // be read as "fits nothing".
      if (!item || item.u) {
        this.clearCard(card);
        continue;
      }

      if (indexItemFits(item, vehicle)) {
        fits += 1;
        this.setCard(card, false, false, `Fits your ${vehicle.model}`, 'fits');
        continue;
      }

      // onlyFits is always true on a tag URL: the tag URL is the filter.
      if (onlyFits) {
        hidden += 1;
        this.setCard(card, true, false, null, null);
      } else {
        this.setCard(card, false, true, "Doesn't fit your truck", 'misfit');
      }
    }

    if (this.summaryEl) {
      const text = fitSummary(vehicle, { hidden, fits });
      // A role="status" region, and apply() runs on every grid mutation:
      // rewriting unchanged text would replace the text node and can make a
      // screen reader announce it again.
      if (this.summaryEl.textContent !== text) {
        this.summaryEl.textContent = text;
        delete this.summaryEl.dataset.mismatch;
      }
      this.summaryEl.hidden = false;
    }
    if (this.actionsEl) this.actionsEl.hidden = false;
    // The toggle is only meaningful on a plain collection URL; a tag URL
    // already hides the misfits.
    if (this.toggleWrap) this.toggleWrap.hidden = this.onTagUrl;
  }

  /**
   * The status line for a tag URL that is not the saved truck's. Rebuilt only
   * when it changes, for the same live-region reason as the summary in apply().
   * @param {{text: string, linkText: string, href: string}} mismatch
   */
  showMismatch({ text, linkText, href }) {
    if (!this.summaryEl) return;
    const signature = `${text}\n${href}`;
    if (this.summaryEl.dataset.mismatch !== signature) {
      const link = document.createElement('a');
      link.className = 'eagle-collection-fit__link';
      link.href = href;
      link.textContent = linkText;
      this.summaryEl.replaceChildren(`${text} `, link);
      this.summaryEl.dataset.mismatch = signature;
    }
    this.summaryEl.hidden = false;
  }

  /** Keeps the checkbox saying what the grid is actually doing. */
  showOnlyFits(checked) {
    if (this.toggle && this.toggle.checked !== checked) this.toggle.checked = checked;
  }

  /**
   * Clears only the state THIS element applied, leaving everything else alone.
   * It deliberately does not unhide a card it did not hide — Horizon may have
   * its own reasons for hiding one.
   */
  clearCard(card) {
    if (card.dataset.eagleHidden) {
      card.hidden = false;
      delete card.dataset.eagleHidden;
    }
    if (card.dataset.eagleDimmed) delete card.dataset.eagleDimmed;
    const existing = card.querySelector('[data-role="eagle-badge"]');
    if (existing) existing.remove();
  }

  /**
   * Puts a card into the wanted state, touching the DOM only where it already
   * differs. This is what makes the MutationObserver converge: apply() runs
   * from an observer on #ResultsList, so an unconditional badge remove+insert
   * would retrigger it forever. The badge is real text inside the card's list
   * item, not a title attribute, which screen readers skip.
   *
   * @param {HTMLElement} card
   * @param {boolean} hidden
   * @param {boolean} dimmed
   * @param {string|null} text   null means the card should carry no badge
   * @param {string|null} kind   'fits' | 'misfit'
   */
  setCard(card, hidden, dimmed, text, kind) {
    if (card.hidden !== hidden) card.hidden = hidden;
    if (hidden) card.dataset.eagleHidden = 'true';
    else delete card.dataset.eagleHidden;
    if (dimmed) card.dataset.eagleDimmed = 'true';
    else delete card.dataset.eagleDimmed;

    const existing = card.querySelector('[data-role="eagle-badge"]');
    if (!text) {
      if (existing) existing.remove();
      return;
    }

    const className = `eagle-card-badge eagle-card-badge--${kind}`;
    if (existing) {
      if (existing.className !== className) existing.className = className;
      if (existing.textContent !== text) existing.textContent = text;
      return;
    }

    const badge = document.createElement('p');
    badge.className = className;
    badge.dataset.role = 'eagle-badge';
    badge.textContent = text;
    card.prepend(badge);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('eagle-collection-fit')) {
  customElements.define('eagle-collection-fit', EagleCollectionFit);
}

/* ------------------------------------------- DOM: quick-add hand-off -- */

/**
 * Where a quick-add "Choose" click should take this product: 'page' for a part
 * that fits only some trucks, 'dialog' for anything else, or 'unknown' when
 * there is no index yet to say.
 *
 * @param {?Array<IndexItem>} items
 * @param {string|number} productId
 * @returns {'page'|'dialog'|'unknown'}
 */
export function quickAddRoute(items, productId) {
  if (!Array.isArray(items)) return 'unknown';
  const item = items.find((entry) => String(entry.id) === String(productId));
  return item && !item.u && Array.isArray(item.v) && item.v.length > 0 ? 'page' : 'dialog';
}

/** The cached index if it can be reused, read synchronously; null otherwise. */
function peekIndex() {
  try {
    return readIndexCache(JSON.parse(storage ? storage.getItem(INDEX_KEY) : null));
  } catch (error) {
    return null;
  }
}

/**
 * Horizon's quick-add builds its dialog from the product page, but on a phone
 * it removes .product-details, and the fit box with it. Horizon's variant
 * picker then comes back and nothing gates Add to cart, so any part could be
 * added for any truck. For a part that fits only some trucks, "Choose" goes to
 * the product page instead, on every screen size: the fit box works there. It
 * is what Horizon itself does for selling-plan products.
 *
 * Horizon handles on:click with a capture listener on document, so this one
 * listens on window, which the capture phase reaches first. No stock file is
 * edited. The one gap is a click in the moment before this module has run.
 */
function onQuickAddChoose(event) {
  const button = event.target instanceof Element ? event.target.closest('.quick-add__button--choose') : null;
  const component = button ? button.closest('quick-add-component') : null;
  if (!component) return;

  const card = component.closest('product-card');
  const cardLink = card ? card.querySelector('a[href*="/products/"]') : null;
  const url = component.productPageUrl || (cardLink ? cardLink.href : '');
  if (!url) return;

  const productId = component.dataset.productId;
  const route = quickAddRoute(peekIndex(), productId);
  if (route === 'dialog') return;

  event.preventDefault();
  event.stopPropagation();
  if (route === 'page') {
    window.location.assign(url);
    return;
  }

  // Not known yet. Hold the click until the index says; if it cannot be had,
  // the product page is the safe side.
  loadIndex().then(
    (items) => {
      if (quickAddRoute(items, productId) === 'page' || typeof component.handleClick !== 'function') {
        window.location.assign(url);
      } else {
        component.handleClick(new Event('click', { cancelable: true }));
      }
    },
    () => window.location.assign(url)
  );
}

if (typeof window !== 'undefined' && !window.__eagleQuickAddHandoff) {
  window.__eagleQuickAddHandoff = true;
  window.addEventListener('click', onQuickAddChoose, { capture: true });
}

/* ------------------------------------ DOM: eagle-fit-recommendations -- */

/**
 * The product page's recommendations row. sections/eagle-fit-recommendations
 * renders a same-category baseline in Liquid; with a saved vehicle this swaps
 * in parts that fit it, ranked by rankRecommendations() and topped up from the
 * baseline, and puts the baseline back when the vehicle is cleared. The top-up
 * takes only baseline cards verifiedTopUp() confirms fit or are universal, so
 * the fitted row can be shorter than the limit: a short row is right, a card
 * that may not fit under a "fits your truck" heading is not.
 *
 * card() reproduces the section's card markup. Change both together.
 */
/**
 * Previous/Next for a recommendations row that scrolls sideways
 * (sections/eagle-fit-recommendations.liquid, and the cart page's
 * snippets/eagle-cart-recommendations.liquid). The buttons are
 * [data-role="prev"] and [data-role="next"] inside [data-role="controls"];
 * the controls show only while the row overflows, and each button is
 * aria-disabled at its end. A row without controls is left alone.
 */
class RowCarousel {
  /**
   * @param {HTMLElement} root  the element holding the controls
   * @param {HTMLElement} list  the scrolling list of cards
   */
  constructor(root, list) {
    this.list = list;
    this.controls = root.querySelector('[data-role="controls"]');
    this.prevButton = root.querySelector('[data-role="prev"]');
    this.nextButton = root.querySelector('[data-role="next"]');
    this.pageTarget = null;
    if (!this.controls) return;
    this.onScroll = () => this.update();
    this.onPrev = () => this.page(-1);
    this.onNext = () => this.page(1);
    this.list.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.list);
    }
    this.update();
  }

  disconnect() {
    if (!this.controls) return;
    this.list.removeEventListener('scroll', this.onScroll);
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    clearTimeout(this.pageTimer);
  }

  /** New cards: back to the start. */
  reset() {
    this.list.scrollLeft = 0;
    this.pageTarget = null;
    this.update();
  }

  /**
   * Scrolls the row back (-1) or on (1) by the cards fully in view, landing
   * on a card's edge. Computed from the card pitch rather than left to
   * scroll-snap, which in Chrome can leave a smooth scrollTo() short of the
   * snap point with a sliver of the previous card showing.
   */
  page(direction) {
    const button = direction < 0 ? this.prevButton : this.nextButton;
    if (button && button.getAttribute('aria-disabled') === 'true') return;
    const [first, second] = this.list.children;
    if (!first || !second) return;
    const pitch = second.offsetLeft - first.offsetLeft;
    if (pitch <= 0) return;
    const style = getComputedStyle(this.list);
    const inner = this.list.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const perView = Math.max(1, Math.floor((inner + (parseFloat(style.columnGap) || 0) + 1) / pitch));
    // A second click while the first is still scrolling pages on from where
    // that scroll is heading, not from wherever the animation has reached.
    const room = this.list.scrollWidth - this.list.clientWidth;
    const from = this.pageTarget == null ? this.list.scrollLeft : this.pageTarget;
    const left = Math.min(room, Math.max(0, (Math.round(from / pitch) + direction * perView) * pitch));
    const still = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.pageTarget = left;
    clearTimeout(this.pageTimer);
    this.pageTimer = setTimeout(() => {
      this.pageTarget = null;
    }, 1000);
    this.list.scrollTo({ left, behavior: still ? 'auto' : 'smooth' });
  }

  /**
   * Shows Previous/Next only when the row overflows, and marks each disabled
   * at its end. aria-disabled, not disabled: a disabled button drops the focus
   * of a keyboard user who has just paged to the end. Writes only what
   * changes, so an observer on the row (the cart's) sees nothing otherwise.
   */
  update() {
    if (!this.controls) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.list;
    const room = scrollWidth - clientWidth;
    const set = (el, name, value) => {
      if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
    };
    if (this.controls.hidden !== room <= 1) this.controls.hidden = room <= 1;
    set(this.prevButton, 'aria-disabled', String(scrollLeft <= 1));
    set(this.nextButton, 'aria-disabled', String(scrollLeft >= room - 1));
  }
}

class EagleFitRecommendations extends HTMLElementBase {
  connectedCallback() {
    this.list = this.querySelector('[data-role="list"]');
    this.headingEl = this.querySelector('[data-role="heading"]');
    this.shopAll = this.querySelector('[data-role="shop-all"]');
    if (!this.list) return;

    this.baseline = [...this.list.children].map((li) => li.cloneNode(true));
    // The list already shows the baseline: mark it so, and a page that falls
    // back to it does not rebuild identical cards (and re-create every <img>).
    this.list.dataset.signature = 'baseline';
    this.baselineHeading = this.headingEl ? this.headingEl.textContent : '';
    this.baselineHidden = this.hidden;
    this.currentId = this.dataset.productId || '';
    try {
      this.currentCats = JSON.parse(this.dataset.categories || '[]');
    } catch (error) {
      this.currentCats = [];
    }
    this.limit = Number(this.dataset.limit) || 12;
    this.endpoint = this.dataset.endpoint || '/collections/all';
    this.root = (this.dataset.root || '/').replace(/\/$/, '');
    // Search & Discovery picks, as <li> cards. Complementary picks are the
    // whole row when there are any; related picks otherwise lead it.
    this.complementary = [];
    this.related = [];

    this.carousel = new RowCarousel(this, this.list);

    this.onVehicleChange = () => this.refresh();
    document.addEventListener('eagle:vehicle-change', this.onVehicleChange);
    window.addEventListener('storage', this.onVehicleChange);
    // pageshow restores a page from the back/forward cache holding the
    // vehicle it had when the shopper left, and they may have changed it
    // since. The extra refresh on first load is a no-op: render()'s
    // signature check skips a DOM write when nothing has changed.
    window.addEventListener('pageshow', this.onVehicleChange);
    this.refresh();
    this.loadRecommended();
  }

  /**
   * Fetches this section through the Product Recommendations API, the only
   * render in which Liquid fills `recommendations`, and keeps its cards: the
   * complementary picks (staff-chosen only) and the related ones (staff-chosen
   * or automatic), apart. Once per page view: a vehicle change reuses them. A
   * list that fails or comes back empty adds nothing.
   */
  loadRecommended() {
    const base = this.dataset.recommendationsUrl;
    const sectionId = this.dataset.sectionId;
    if (!base || !sectionId || !this.currentId || typeof fetch !== 'function') return;
    const cardsFor = (intent) => {
      const params = new URLSearchParams({
        product_id: this.currentId,
        section_id: sectionId,
        intent,
        limit: '10',
      });
      return fetch(`${base}?${params}`)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return [...doc.querySelectorAll('[data-role="recommended"] > li[data-product-id]')];
        })
        .catch(() => []);
    };
    Promise.all([cardsFor('complementary'), cardsFor('related')]).then(([complementary, related]) => {
      if (!complementary.length && !related.length) return;
      // Repeats across the two lists are dropped by recommendationRow().
      this.complementary = complementary.map((li) => document.importNode(li, true));
      this.related = related.map((li) => document.importNode(li, true));
      this.refresh();
    });
  }

  /** Every Search & Discovery pick, complementary then related. */
  get picks() {
    return [...this.complementary, ...this.related];
  }

  /**
   * The row via recommendationRow(): the complementary picks alone when any
   * count, otherwise the related picks, then `rest`. A pick's own card is used
   * even when the same product is also in `rest`.
   */
  arrange(rest, vehicle = null, items = []) {
    const pool = new Map();
    for (const li of [...rest, ...this.related, ...this.complementary]) pool.set(String(li.dataset.productId), li);
    const idsOf = (cards) => cards.map((li) => li.dataset.productId);
    const ids = recommendationRow(idsOf(this.complementary), idsOf(this.related), idsOf(rest), {
      limit: this.limit,
      currentId: this.currentId,
      vehicle,
      items,
    });
    return { ids, nodes: ids.map((id) => pool.get(id).cloneNode(true)) };
  }

  disconnectedCallback() {
    document.removeEventListener('eagle:vehicle-change', this.onVehicleChange);
    window.removeEventListener('storage', this.onVehicleChange);
    window.removeEventListener('pageshow', this.onVehicleChange);
    if (this.carousel) this.carousel.disconnect();
  }

  /**
   * With a saved truck, parts that fit it; without one, parts that share a
   * fitment with this product. Either way the Search & Discovery picks lead,
   * and complementary picks, where any count, are the whole row (see
   * recommendationRow). Both need the vehicle index.
   */
  refresh() {
    if (!this.list) return;
    // A slower, older refresh must not overwrite a newer one.
    const token = {};
    this.refreshToken = token;
    loadIndex(this.endpoint)
      .then((items) => {
        if (this.refreshToken !== token) return;
        const vehicle = getVehicle();
        const current = items.find((item) => item && String(item.id) === String(this.currentId));
        const ranked = rankRecommendations(items, {
          vehicle,
          currentId: this.currentId,
          currentCats: this.currentCats,
          fitment: current ? current.v : [],
          limit: this.limit,
        });
        if (vehicle) this.showFitted(vehicle, ranked, items);
        else this.showShared(ranked);
      })
      .catch(() => {
        // An unreachable index leaves the server-rendered row, which is complete.
        if (this.refreshToken === token) this.showBaseline();
      });
  }

  showBaseline() {
    this.refreshToken = null;
    const { ids, nodes } = this.arrange(this.baseline);
    // Plain 'baseline' matches the mark set on connect, so a page with no
    // Search & Discovery picks never rebuilds the server-rendered cards.
    this.showUnfitted(nodes, this.picks.length ? `baseline|${ids.join(',')}` : 'baseline');
  }

  /**
   * No saved truck: the picks, then parts that share a fitment with this
   * product. Nothing shared (a universal product, say) leaves the baseline.
   */
  showShared(ranked) {
    if (!ranked.length) {
      this.showBaseline();
      return;
    }
    const { ids, nodes } = this.arrange(ranked.map((item) => this.card(item)));
    this.showUnfitted(nodes, `shared|${ids.join(',')}`);
  }

  /** A row under the general heading, with no shop-all link. */
  showUnfitted(nodes, signature) {
    this.render(nodes, signature);
    if (this.headingEl && this.headingEl.textContent !== this.baselineHeading) {
      this.headingEl.textContent = this.baselineHeading;
    }
    if (this.shopAll) this.shopAll.hidden = true;
    this.hidden = this.baselineHidden && !nodes.length;
  }

  showFitted(vehicle, ranked, items) {
    const seen = new Set(ranked.map((item) => String(item.id)));
    const cards = ranked.map((item) => this.card(item));
    const baselineIds = this.baseline.map((li) => li.dataset.productId);
    const extra = verifiedTopUp(baselineIds, items, vehicle, seen, this.limit - cards.length);
    for (const id of extra) {
      const li = this.baseline.find((node) => String(node.dataset.productId) === id);
      if (li) {
        seen.add(id);
        cards.push(li.cloneNode(true));
      }
    }
    const { ids, nodes } = this.arrange(cards, vehicle, items);
    // Nothing the index ranked and no pick that fits: the baseline
    // stands, as it did before Search & Discovery picks existed.
    const pickIds = new Set(this.picks.map((li) => String(li.dataset.productId)));
    if (!ranked.length && !ids.some((id) => pickIds.has(id))) {
      this.showBaseline();
      return;
    }
    this.render(nodes, `fit|${vehicle.year}|${vehicle.make}|${vehicle.model}|${ids.join(',')}`);

    const heading = `${this.dataset.fitPrefix || 'Recommended Parts that fit your'} ${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    if (this.headingEl && this.headingEl.textContent !== heading) this.headingEl.textContent = heading;
    if (this.shopAll) {
      this.shopAll.href = `${this.endpoint}/${vehicleTagHandle(vehicle)}`;
      this.shopAll.hidden = false;
    }
    this.hidden = false;
  }

  /** Replaces the list only when its contents actually change. */
  render(nodes, signature) {
    if (this.list.dataset.signature === signature) return;
    this.list.dataset.signature = signature;
    this.list.replaceChildren(...nodes);
    this.carousel.reset();
  }

  /** One card, in the markup of snippets/eagle-rec-card.liquid. Text via textContent only. */
  card(item) {
    const li = document.createElement('li');
    li.className = 'eagle-rec__item';
    li.dataset.productId = String(item.id);

    const link = document.createElement('a');
    link.className = 'eagle-rec__card';
    link.href = `${this.root}/products/${encodeURIComponent(item.h)}`;

    const media = document.createElement('span');
    media.className = 'eagle-rec__media';
    if (item.i) {
      const img = document.createElement('img');
      img.className = 'eagle-rec__img';
      img.src = item.i;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 600;
      img.height = 600;
      media.append(img);
    }

    const title = document.createElement('span');
    title.className = 'eagle-rec__title';
    title.textContent = item.t || '';

    const price = document.createElement('span');
    price.className = 'eagle-rec__price';
    price.textContent = item.pv ? `${this.dataset.fromLabel || 'From'} ${item.p}` : item.p || '';
    appendWasPrice(price, item.cp, this.dataset.wasLabel || 'Regular price');

    link.append(media, title, price);
    li.append(link);

    // Bottom-right cart icon. It adds in one click only where cartCardAction
    // allows; on any other part it is part of the card's link and opens the
    // product page to choose.
    const action = cartCardAction(item);
    if (action.kind === 'add') {
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'eagle-rec__add';
      add.dataset.variantId = action.variantId;
      add.setAttribute('aria-label', `${this.dataset.addLabel || 'Add to cart:'} ${item.t || ''}`);
      add.append(addToCartIcon());
      li.append(add);
    } else {
      const icon = document.createElement('span');
      icon.className = 'eagle-rec__add';
      icon.setAttribute('aria-hidden', 'true');
      icon.append(addToCartIcon());
      link.append(icon);
    }
    return li;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('eagle-fit-recommendations')) {
  customElements.define('eagle-fit-recommendations', EagleFitRecommendations);
}

/* ------------------------------------------------ cart recommendations -- */

/**
 * One product per category in turn, categories in the order of their first
 * title and products within one by title then id — the ordering
 * rankRecommendations() uses, so one large family cannot fill the row and the
 * row does not reshuffle between views.
 * @param {IndexItem[]} items
 * @returns {IndexItem[]}
 */
function oneCategoryAtATime(items) {
  const byTitle = (a, b) =>
    String(a.t || '').localeCompare(String(b.t || ''), 'en') || String(a.id).localeCompare(String(b.id));
  const groups = new Map();
  for (const item of [...items].sort(byTitle)) {
    const key = (item.c && item.c[0]) || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  const queues = [...groups.values()];
  const out = [];
  for (let round = 0; queues.some((q) => q.length > round); round += 1) {
    for (const q of queues) if (q.length > round) out.push(q[round]);
  }
  return out;
}

/**
 * The products the cart's recommendations row shows (drawer and cart page),
 * as ids in display order. Never a product already in the cart.
 *
 * 1. The Search & Discovery complementary picks for the cart's lines, newest
 *    line first, each in staff order.
 * 2. Their related picks, minus any from a category already in the cart: a
 *    related product is often a substitute (a second box cover), the wrong
 *    thing to offer someone who has just chosen one.
 * 3. The catalogue, from categories not in the cart: parts that fit the saved
 *    truck, then universal parts.
 *
 * With a saved truck every card must fit it or be universal, as on the product
 * page: the heading says so, and a pick missing from the index is unchecked.
 * With none, "fits" means shares a fitment with the newest cart line that
 * lists trucks (`fitment`, its index entries; see sharesFitment), as the
 * product page's row does; the picks stand, and a card for a part that fits
 * only some trucks sends the shopper to its product page to choose
 * (cartCardAction).
 *
 * @param {{complementary?: Array<number|string>, related?: Array<number|string>, items?: IndexItem[], cartIds?: Array<number|string>, vehicle?: ?Vehicle, fitment?: Array<[string,string,number,number]>, limit?: number}} options
 * @returns {string[]}
 */
export function cartRecommendations({ complementary = [], related = [], items = [], cartIds = [], vehicle = null, fitment = [], limit = 3 } = {}) {
  const byId = new Map((items || []).map((item) => [String(item.id), item]));
  const taken = new Set((cartIds || []).map(String));
  const cartCats = new Set();
  for (const id of taken) for (const handle of (byId.get(id) || {}).c || []) cartCats.add(String(handle));
  const inCartCategory = (item) => Boolean(item) && (item.c || []).some((handle) => cartCats.has(String(handle)));
  const fitsTruck = (item) => !vehicle || Boolean(item && (item.u || indexItemFits(item, vehicle)));

  const out = [];
  const place = (raw, ok) => {
    const id = String(raw);
    if (out.length >= limit || taken.has(id) || !ok(byId.get(id))) return;
    taken.add(id);
    out.push(id);
  };
  for (const id of complementary || []) place(id, fitsTruck);
  for (const id of related || []) place(id, (item) => fitsTruck(item) && !inCartCategory(item));

  let fits = () => false;
  if (vehicle) fits = (item) => indexItemFits(item, vehicle);
  else if (fitment && fitment.length) fits = (item) => sharesFitment(item, fitment);
  const fitted = [];
  const universal = [];
  for (const item of items || []) {
    if (!item || taken.has(String(item.id)) || inCartCategory(item)) continue;
    if (fits(item)) fitted.push(item);
    else if (item.u) universal.push(item);
  }
  for (const item of [...oneCategoryAtATime(fitted), ...oneCategoryAtATime(universal)]) place(item.id, () => true);
  return out;
}

/**
 * What a cart recommendation card's button does. One click adds only a part
 * that fits any truck and has a single variant: anything that fits some trucks
 * has conditions of fit (cab, tailgate, body) only the product page's fit box
 * states, and a family has a part to choose — the rule the quick-add hand-off
 * applies (quickAddRoute). Everything else links to the product page.
 *
 * @param {?IndexItem} item  the index entry, or null for a product not in it
 * @returns {{kind: 'add', variantId: string} | {kind: 'choose'}}
 */
export function cartCardAction(item) {
  if (item && item.s && (item.u || !(item.v || []).length)) return { kind: 'add', variantId: String(item.s) };
  return { kind: 'choose' };
}

/**
 * Product ids from an Ajax cart's lines, newest first and without repeats.
 * @param {?{items?: Array<{product_id: number|string}>}} cart
 * @returns {string[]}
 */
export function cartProductIds(cart) {
  const ids = [];
  for (const line of (cart && cart.items) || []) {
    const id = String(line.product_id);
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** The standard event Horizon's cart components listen for (@shopify/events). */
const CART_LINES_UPDATE = 'shopify:cart:lines-update';

/** How many cart lines, newest first, the row reads Search & Discovery picks for: two requests each. */
const MAX_PICK_LINES = 5;

/**
 * Adds one of a variant the way Horizon's product form does: announce the
 * change with a CartLinesUpdateEvent dispatched from `source`, post to
 * /cart/add.js asking for the cart sections' new HTML, and settle the event's
 * promise with the new cart, so the drawer (which opens on an add), the cart
 * page and the header count update themselves.
 *
 * @param {Element} source  where the event starts; it bubbles to document
 * @param {{variantId: string, productId?: string, root?: string}} options
 * @returns {Promise<{ok: boolean, message: string, cart: ?Object}>} message is the store's reason when ok is false
 */
export async function addVariantToCart(source, { variantId, productId = '', root = '' }) {
  let Update = null;
  try {
    Update = (await import('@shopify/events')).CartLinesUpdateEvent || null;
  } catch (error) {
    Update = null;
  }
  const deferred = Update ? Update.createPromise() : null;
  if (deferred) {
    source.dispatchEvent(
      new Update({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );
  }
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);

  try {
    const response = await fetch(`${root}/cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sections.join(',') }),
    });
    const result = await response.json();
    const cart = await fetch(`${root}/cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const ok = response.ok && !result.status;
    if (deferred) {
      deferred.resolve({
        cart: Update.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'eagle-add-to-cart',
          itemCount: 1,
          productId,
          sections: ok ? result.sections : undefined,
          didError: !ok,
        },
      });
    }
    return { ok, message: ok ? '' : result.description || result.message || '', cart };
  } catch (error) {
    if (deferred) deferred.reject(error);
    return { ok: false, message: '', cart: null };
  }
}

/**
 * Horizon's add-to-cart icon (assets/icon-add-to-cart.svg), for cards built
 * in JavaScript. Decorative: the control around it carries the name.
 * @returns {SVGSVGElement}
 */
function addToCartIcon() {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('fill', 'none');
  for (const d of [
    'M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3',
    'M14.608 12.563v5m2.5-2.5h-5',
  ]) {
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', 'var(--icon-stroke-width)');
    path.setAttribute('d', d);
    svg.append(path);
  }
  return svg;
}

/**
 * The product-page row's add-to-cart icons (snippets/eagle-rec-card.liquid,
 * EagleFitRecommendations.card()). One delegated listener for the page, as
 * the quick-add hand-off uses, so the row's own lifecycle is untouched.
 * aria-disabled rather than disabled while it works: disabling the focused
 * button would drop keyboard focus to the page.
 */
function onRecAddClick(event) {
  const button = event.target instanceof Element ? event.target.closest('button.eagle-rec__add[data-variant-id]') : null;
  if (!button || button.getAttribute('aria-disabled') === 'true') return;
  const row = button.closest('eagle-fit-recommendations');
  const status = row ? row.querySelector('[data-role="status"]') : null;
  const card = button.closest('[data-product-id]');
  const root = row ? (row.dataset.root || '/').replace(/\/$/, '') : '';
  button.setAttribute('aria-disabled', 'true');
  button.setAttribute('aria-busy', 'true');
  if (status) status.textContent = '';
  addVariantToCart(button, {
    variantId: button.dataset.variantId,
    productId: card ? card.dataset.productId : '',
    root,
  }).then(({ ok, message }) => {
    button.removeAttribute('aria-disabled');
    button.removeAttribute('aria-busy');
    if (!ok && status) {
      status.textContent = message || (row && row.dataset.addError) || 'Could not add this part. Please try again.';
    }
  });
}

if (typeof window !== 'undefined' && !window.__eagleRecAdd) {
  window.__eagleRecAdd = true;
  document.addEventListener('click', onRecAddClick);
}

/* -------------------------------- DOM: eagle-cart-recommendations -- */

/**
 * The "parts that go with your order" row, in the cart drawer
 * (snippets/cart-drawer.liquid) and on the cart page
 * (sections/eagle-cart-recommendations.liquid). Markup:
 * snippets/eagle-cart-recommendations.liquid. Which products:
 * cartRecommendations().
 *
 * Horizon re-renders the drawer by morphing it against fresh server HTML,
 * which puts this element's server markup — empty and hidden — back. A
 * MutationObserver puts the rendered row straight back; its callback runs
 * before the next paint, so the row never flickers.
 */
class EagleCartRecommendations extends HTMLElementBase {
  connectedCallback() {
    this.list = this.querySelector('[data-role="list"]');
    this.headingEl = this.querySelector('[data-role="heading"]');
    this.statusEl = this.querySelector('[data-role="status"]');
    if (!this.list) return;

    this.limit = Number(this.dataset.limit) || 3;
    this.endpoint = this.dataset.endpoint || '/collections/all';
    this.root = (this.dataset.root || '/').replace(/\/$/, '');
    this.cartIds = this.readCartIds();
    this.renderedCart = this.dataset.cartProducts;
    /** @type {Map<string, Promise<{complementary: string[], related: string[], products: Map<string, Object>}>>} */
    this.picks = new Map();
    this.view = { hidden: true, heading: '', nodes: [], signature: '' };
    this.status = '';
    this.focused = null;
    // The cart page's row scrolls sideways; the drawer's list has no controls.
    this.carousel = new RowCarousel(this, this.list);

    this.observer = new MutationObserver(() => this.apply());
    this.observer.observe(this, { childList: true, subtree: true, attributes: true, characterData: true });

    this.onRefresh = () => this.refresh();
    this.onCartUpdate = (event) => this.cartUpdated(event);
    this.onClick = (event) => this.clicked(event);
    this.onFocusIn = (event) => {
      this.focused = event.target;
    };
    // Forget the focused control once focus has really left the row. A morph
    // takes it out of the page for a moment and apply() puts it back, so the
    // check waits a task.
    this.onFocusOut = () => {
      setTimeout(() => {
        if (this.focused && this.focused.isConnected && !this.contains(document.activeElement)) this.focused = null;
      }, 0);
    };
    document.addEventListener('eagle:vehicle-change', this.onRefresh);
    document.addEventListener(CART_LINES_UPDATE, this.onCartUpdate);
    window.addEventListener('storage', this.onRefresh);
    window.addEventListener('pageshow', this.onRefresh);
    this.addEventListener('click', this.onClick);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.refresh();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.carousel) this.carousel.disconnect();
    document.removeEventListener('eagle:vehicle-change', this.onRefresh);
    document.removeEventListener(CART_LINES_UPDATE, this.onCartUpdate);
    window.removeEventListener('storage', this.onRefresh);
    window.removeEventListener('pageshow', this.onRefresh);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
  }

  /** The cart's product ids as the server rendered them, newest first. */
  readCartIds() {
    try {
      const ids = JSON.parse(this.dataset.cartProducts || '[]');
      return Array.isArray(ids) ? ids.map(String) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Any cart change, from anywhere: once it settles, read the cart. The drawer
   * also learns it from its morph, but the cart page's row sits in a section
   * of its own, which nothing re-renders.
   */
  cartUpdated(event) {
    const settled = event && event.promise ? event.promise.catch(() => null) : Promise.resolve();
    settled
      .then(() => fetch(`${this.root}/cart.js`, { headers: { Accept: 'application/json' } }))
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
      .then((cart) => this.setCart(cartProductIds(cart)))
      .catch(() => {});
  }

  /** A different cart clears the last add's message, unless this is that add's own cart. */
  setCart(ids, keepStatus = false) {
    if (ids.join(',') === this.cartIds.join(',')) return;
    this.cartIds = ids;
    if (!keepStatus) this.status = '';
    this.refresh();
  }

  /**
   * The Search & Discovery picks for one product: this element's section
   * fetched through the Product Recommendations API, which writes them as
   * JSON. Complementary and related, once per product per page view; a failed
   * or empty list is no picks.
   */
  picksFor(productId) {
    if (!this.picks.has(productId)) {
      const base = this.dataset.recommendationsUrl;
      const sectionId = this.dataset.sectionId;
      const intent = (name) => {
        if (!base || !sectionId || typeof fetch !== 'function') return Promise.resolve([]);
        const params = new URLSearchParams({ product_id: productId, section_id: sectionId, intent: name, limit: '10' });
        return fetch(`${base}?${params}`)
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const data = doc.querySelector('script[data-role="recommended"]');
            const list = data ? JSON.parse(data.textContent || '[]') : [];
            return Array.isArray(list) ? list : [];
          })
          .catch(() => []);
      };
      this.picks.set(
        productId,
        Promise.all([intent('complementary'), intent('related')]).then(([complementary, related]) => ({
          complementary: complementary.map((product) => String(product.id)),
          related: related.map((product) => String(product.id)),
          products: new Map([...related, ...complementary].map((product) => [String(product.id), product])),
        }))
      );
    }
    return this.picks.get(productId);
  }

  refresh() {
    if (!this.list) return;
    const token = {};
    this.refreshToken = token;
    const cartIds = this.cartIds;
    if (!cartIds.length) {
      this.show({ hidden: true, heading: '', nodes: [], signature: '' });
      return;
    }
    // Every line's picks, newest first: adding one of a product's picks makes
    // that pick the newest line, and it seldom has picks of its own, so
    // reading the newest line alone dropped the rest (a whip's other mounts).
    const lines = cartIds.slice(0, MAX_PICK_LINES);
    Promise.all([loadIndex(this.endpoint).catch(() => []), ...lines.map((id) => this.picksFor(id))]).then(([items, ...perLine]) => {
      if (this.refreshToken !== token) return;
      const vehicle = getVehicle();
      const byId = new Map(items.map((item) => [String(item.id), item]));
      const picks = {
        complementary: perLine.flatMap((line) => line.complementary),
        related: perLine.flatMap((line) => line.related),
        products: new Map(perLine.flatMap((line) => [...line.products])),
      };
      const listed = cartIds.map((id) => byId.get(id)).find((item) => item && (item.v || []).length);
      const ids = cartRecommendations({
        complementary: picks.complementary,
        related: picks.related,
        items,
        cartIds,
        vehicle,
        fitment: listed ? listed.v : [],
        limit: this.limit,
      });
      const heading = vehicle
        ? `${this.dataset.fitPrefix || 'Recommended Parts that fit your'} ${vehicle.year} ${vehicle.make} ${vehicle.model}`
        : this.dataset.heading || 'Goes with your order';
      const signature = `${heading}|${ids.join(',')}`;
      if (signature === this.view.signature) return;
      const nodes = ids.map((id) => this.card(byId.get(id) || picks.products.get(id), byId.has(id)));
      this.show({ hidden: !nodes.length, heading, nodes, signature });
    });
  }

  show(view) {
    this.view = view;
    this.apply();
  }

  /** Writes the current view into the DOM, touching only what differs. */
  apply() {
    if (!this.list || !this.view) return;
    const { hidden, heading, nodes } = this.view;

    if (this.hidden !== hidden) this.hidden = hidden;
    if (this.headingEl && this.headingEl.textContent !== heading) this.headingEl.textContent = heading;
    const current = [...this.list.children];
    if (current.length !== nodes.length || current.some((node, i) => node !== nodes[i])) {
      this.list.replaceChildren(...nodes);
      if (this.carousel) this.carousel.reset();
    }
    if (this.statusEl && this.statusEl.textContent !== this.status) this.statusEl.textContent = this.status;

    // Only a new data-cart-products from the server (the drawer's morph) says
    // the cart changed. The cart page never re-renders this element, so its
    // value there is the cart at page load, older than what cartUpdated() read.
    // Checked here, not only in the observer: the takeRecords() below can
    // swallow a morph's records when a refresh lands in the same microtask
    // checkpoint.
    const serverCart = this.dataset.cartProducts;
    const cartChanged = serverCart !== this.renderedCart;
    this.renderedCart = serverCart;

    // A morph or a new row took the focused control out of the page: put focus
    // back on it, or on the heading when its card has gone (a part just added
    // leaves the row).
    const lostFocus = Boolean(this.focused) && (!document.activeElement || document.activeElement === document.body);
    if (lostFocus && !hidden) {
      const target = this.focused.isConnected ? this.focused : this.headingEl;
      if (target) target.focus({ preventScroll: true });
    }
    if (this.observer) this.observer.takeRecords();
    if (cartChanged) this.setCart(this.readCartIds());
  }

  /**
   * One card. Text via textContent only.
   * @param {Object} item  an index item, or a Search & Discovery pick's JSON
   * @param {boolean} indexed  whether `item` came from the index
   */
  card(item, indexed) {
    const url = `${this.root}/products/${encodeURIComponent(item.h)}`;
    const title = item.t || '';
    const li = document.createElement('li');
    li.className = 'eagle-cart-rec__item';
    li.dataset.productId = String(item.id);

    const media = document.createElement('span');
    media.className = 'eagle-cart-rec__media';
    if (item.i) {
      const img = document.createElement('img');
      const width = Number(this.dataset.imageWidth) || 600;
      img.className = 'eagle-cart-rec__img';
      img.src = String(item.i).replace(/([?&]width=)\d+/, `$1${width}`);
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = width;
      img.height = width;
      media.append(img);
    }

    const body = document.createElement('span');
    body.className = 'eagle-cart-rec__body';
    const link = document.createElement('a');
    link.className = 'eagle-cart-rec__title';
    link.href = url;
    link.textContent = title;
    const price = document.createElement('span');
    price.className = 'eagle-cart-rec__price';
    price.textContent = item.pv ? `${this.dataset.fromLabel || 'From'} ${item.p}` : item.p || '';
    appendWasPrice(price, item.cp, this.dataset.wasLabel || 'Regular price');
    body.append(link, price);

    li.append(media, body, this.iconControl(cartCardAction(indexed ? item : null), url, title));
    return li;
  }

  /**
   * The card's control: Horizon's add-to-cart icon, no text, as on the
   * product page's cards (bottom-right corner on the cart page, the row's end
   * in the drawer). A button where one click can add the part; otherwise a
   * link to the product page that only a pointer reaches (tabindex -1, hidden
   * from assistive tech), because the card's title link already goes there
   * for everyone else.
   */
  iconControl(action, url, title) {
    let control;
    if (action.kind === 'add') {
      control = document.createElement('button');
      control.type = 'button';
      control.dataset.variantId = action.variantId;
      control.setAttribute('aria-label', `${this.dataset.addIconLabel || 'Add to cart:'} ${title}`);
    } else {
      control = document.createElement('a');
      control.href = url;
      control.tabIndex = -1;
      control.setAttribute('aria-hidden', 'true');
    }
    control.className = 'eagle-cart-rec__icon';
    control.append(addToCartIcon());
    return control;
  }

  clicked(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-variant-id]') : null;
    if (!button || !this.contains(button) || button.getAttribute('aria-disabled') === 'true') return;
    const card = button.closest('[data-product-id]');
    const titleEl = card ? card.querySelector('.eagle-cart-rec__title') : null;
    this.add(button, button.dataset.variantId, card ? card.dataset.productId : '', titleEl ? titleEl.textContent : '');
  }

  /**
   * Adds one of the variant the way Horizon's product form does: announce the
   * change with a CartLinesUpdateEvent, post to /cart/add.js asking for the
   * cart sections' new HTML, and settle the event's promise with the new cart,
   * so the drawer, the cart page and the header count update themselves.
   */
  async add(button, variantId, productId, title) {
    // aria-disabled, not disabled: disabling the focused button would drop
    // keyboard focus to the page, and apply() could not tell where it was.
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    const { ok, message, cart } = await addVariantToCart(this, { variantId, productId, root: this.root });
    button.removeAttribute('aria-disabled');
    button.removeAttribute('aria-busy');
    this.status = ok
      ? `${this.dataset.addedLabel || 'Added to your cart:'} ${title}`
      : message || this.dataset.errorLabel || 'Could not add this part. Please try again.';
    this.apply();
    if (ok && cart) this.setCart(cartProductIds(cart), true);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('eagle-cart-recommendations')) {
  customElements.define('eagle-cart-recommendations', EagleCartRecommendations);
}
